/**
 * CompaniesPage - Admin page for managing companies/organizations
 * Features: Overview, Policies, Users, Pricing, Inbound Activity tabs
 */
import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import CountrySelect from '../components/CountrySelect';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../components/ui/dialog';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import VinylOrderModesPanel from '../components/admin/VinylOrderModesPanel';
import PendingOrgApprovalsPanel from '../components/admin/PendingOrgApprovalsPanel';
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
} from '../components/ui/sheet';
import {
    Building2,
    Users,
    FileText,
    Settings,
    DollarSign,
    Inbox,
    ArrowLeft,
    Plus,
    Search,
    CheckCircle,
    XCircle,
    Clock,
    AlertTriangle,
    ExternalLink,
    Edit,
    ChevronRight,
    Library,
    Upload,
    Download,
    Trash2,
    Loader2,
    FileSpreadsheet,
    X,
    AlertCircle,
    Link2,
    Star,
    Tag,
    CreditCard,
    RefreshCw,
    Ban,
    Bell,
    Layers
} from 'lucide-react';
import { MediaDiscIcon } from '../components/MediaDiscIcons';
import { invoiceSyncApi } from '../lib/api';
import { useInvoiceReminder } from '../hooks/useInvoiceReminder';

import { API_BASE } from '../lib/apiBase';
const TABS = [
    { id: 'overview', label: 'Overview', icon: Building2 },
    { id: 'policies', label: 'Policies', icon: Settings },
    { id: 'modules', label: 'Modules', icon: Layers },
    { id: 'users', label: 'Users', icon: Users },
    { id: 'catalogue', label: 'Catalogue Library', icon: Library },
    { id: 'pricing', label: 'Pricing', icon: DollarSign },
    { id: 'invoices', label: 'Invoices & Credit', icon: CreditCard },
    { id: 'inbound', label: 'Inbound Activity', icon: Inbox },
];

const BILLING_STATUS_CONFIG = {
    incomplete: { label: 'Incomplete', color: 'text-slate-500', bg: 'bg-slate-100' },
    pending_approval: { label: 'Pending Approval', color: 'text-amber-600', bg: 'bg-amber-50' },
    synced_to_economic: { label: 'Synced', color: 'text-emerald-600', bg: 'bg-emerald-50' },
};

// ==================== Company List View ====================
function CompanyListView({ onSelectCompany, embedded = false }) {
    const navigate = useNavigate();
    const [companies, setCompanies] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [newCompany, setNewCompany] = useState({ name: '', type: 'business', external_id: '' });
    const [isCreating, setIsCreating] = useState(false);

    useEffect(() => {
        fetchCompanies();
    }, []);

    const fetchCompanies = async () => {
        setIsLoading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(
                `${API_BASE}/api/organizations`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setCompanies(response.data || []);
        } catch (error) {
            toast.error('Failed to load companies');
        } finally {
            setIsLoading(false);
        }
    };

    const handleCreate = async () => {
        if (!newCompany.name.trim()) {
            toast.error('Company name is required');
            return;
        }
        setIsCreating(true);
        try {
            const token = localStorage.getItem('token');
            const response = await axios.post(
                `${API_BASE}/api/admin/organizations`,
                newCompany,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success('Company created');
            setShowCreateModal(false);
            setNewCompany({ name: '', type: 'business', external_id: '' });
            fetchCompanies();
            navigate(`/admin/companies/${response.data.id}`);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to create company');
        } finally {
            setIsCreating(false);
        }
    };

    const filteredCompanies = companies.filter(c => {
        if (!searchQuery) return true;
        const q = searchQuery.toLowerCase();
        return c.name?.toLowerCase().includes(q) || 
               c.external_id?.toLowerCase().includes(q) ||
               c.economic_customer_id?.toLowerCase().includes(q);
    });

    const getBillingStatus = (status) => {
        const config = BILLING_STATUS_CONFIG[status] || BILLING_STATUS_CONFIG.incomplete;
        return (
            <span className={`px-2 py-0.5 rounded text-xs font-medium ${config.bg} ${config.color}`}>
                {config.label}
            </span>
        );
    };

    return (
        <div className={embedded ? '' : 'p-6 max-w-7xl mx-auto'}>
            {!embedded && (
                <button
                    onClick={() => navigate('/dashboard')}
                    className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-4 transition-colors"
                >
                    <ArrowLeft className="h-5 w-5" />
                    <span className="text-sm font-medium">Back</span>
                </button>
            )}

            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                    <Building2 className="h-6 w-6 text-slate-600" />
                    <div>
                        <h1 className="text-xl font-semibold text-slate-900">Companies</h1>
                        <p className="text-sm text-slate-500">Manage organizations and their policies</p>
                    </div>
                </div>
                <Button onClick={() => setShowCreateModal(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    New Company
                </Button>
            </div>

            {/* Pending Organization Approvals — moved from Users page
                (2026-02). Approval + e-conomic sync belongs with the
                company/billing profile, not the person/login. */}
            <PendingOrgApprovalsPanel />

            {/* Search */}
            <div className="mb-4">
                <div className="relative max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                        placeholder="Search by name, CVR, or customer ID..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-9"
                    />
                </div>
            </div>

            {/* Companies Table */}
            <div className="border rounded-lg overflow-hidden">
                <Table>
                    <TableHeader>
                        <TableRow className="bg-background">
                            <TableHead>Company</TableHead>
                            <TableHead className="w-[120px]">External ID</TableHead>
                            <TableHead className="w-[100px]">Type</TableHead>
                            <TableHead className="w-[120px]">Billing Status</TableHead>
                            <TableHead className="w-[80px] text-center">Users</TableHead>
                            <TableHead className="w-[80px] text-center">Orders</TableHead>
                            <TableHead className="w-[60px]"></TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {isLoading ? (
                            <TableRow>
                                <TableCell colSpan={7} className="text-center py-8 text-slate-400">
                                    Loading companies...
                                </TableCell>
                            </TableRow>
                        ) : filteredCompanies.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={7} className="text-center py-8 text-slate-400">
                                    No companies found
                                </TableCell>
                            </TableRow>
                        ) : (
                            filteredCompanies.map((company) => (
                                <TableRow 
                                    key={company.id} 
                                    className="hover:bg-background cursor-pointer"
                                    onClick={() => navigate(`/admin/companies/${company.id}`)}
                                >
                                    <TableCell>
                                        <div>
                                            <p className="font-medium text-slate-900">{company.name}</p>
                                            {company.economic_customer_id && (
                                                <p className="text-xs text-slate-400">{company.economic_customer_id}</p>
                                            )}
                                        </div>
                                    </TableCell>
                                    <TableCell className="text-sm text-slate-600">
                                        {company.external_id || '—'}
                                    </TableCell>
                                    <TableCell>
                                        <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded capitalize">
                                            {company.type || 'private'}
                                        </span>
                                    </TableCell>
                                    <TableCell>{getBillingStatus(company.billing_status)}</TableCell>
                                    <TableCell className="text-center text-sm text-slate-600">
                                        {company.user_count || 0}
                                    </TableCell>
                                    <TableCell className="text-center text-sm text-slate-600">
                                        {company.order_count || 0}
                                    </TableCell>
                                    <TableCell>
                                        <ChevronRight className="h-4 w-4 text-slate-400" />
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </div>

            {/* Create Company Modal */}
            <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>Create New Company</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <label className="text-sm font-medium">Company Name *</label>
                            <Input
                                value={newCompany.name}
                                onChange={(e) => setNewCompany({ ...newCompany, name: e.target.value })}
                                placeholder="Enter company name"
                            />
                        </div>
                        <div className="space-y-2">
                            <label className="text-sm font-medium">Type</label>
                            <Select 
                                value={newCompany.type} 
                                onValueChange={(v) => setNewCompany({ ...newCompany, type: v })}
                            >
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="business">Business</SelectItem>
                                    <SelectItem value="private">Private</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2">
                            <label className="text-sm font-medium">External ID (CVR/Customer ID)</label>
                            <Input
                                value={newCompany.external_id}
                                onChange={(e) => setNewCompany({ ...newCompany, external_id: e.target.value })}
                                placeholder="Optional"
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowCreateModal(false)}>
                            Cancel
                        </Button>
                        <Button onClick={handleCreate} disabled={isCreating}>
                            {isCreating ? 'Creating...' : 'Create Company'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

// ==================== Company Detail View ====================
function CompanyDetailView({ companyId }) {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const activeTab = searchParams.get('tab') || 'overview';
    
    const [company, setCompany] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        fetchCompany();
    }, [companyId]);

    const fetchCompany = async () => {
        setIsLoading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(
                `${API_BASE}/api/organizations/${companyId}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setCompany(response.data);
        } catch (error) {
            toast.error('Failed to load company');
            navigate('/admin/companies');
        } finally {
            setIsLoading(false);
        }
    };

    const setActiveTab = (tab) => {
        setSearchParams({ tab });
    };

    if (isLoading) {
        return (
            <div className="p-6 max-w-7xl mx-auto">
                <div className="text-center py-12 text-slate-400">Loading company...</div>
            </div>
        );
    }

    if (!company) {
        return null;
    }

    return (
        <div className="p-6 max-w-7xl mx-auto">
            {/* Back Button */}
            <button
                onClick={() => navigate('/admin/companies')}
                className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-4 transition-colors"
            >
                <ArrowLeft className="h-5 w-5" />
                <span className="text-sm font-medium">Back to Companies</span>
            </button>

            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                    <Building2 className="h-6 w-6 text-slate-600" />
                    <div>
                        <h1 className="text-xl font-semibold text-slate-900">{company.name}</h1>
                        <p className="text-sm text-slate-500">
                            {company.external_id && `CVR: ${company.external_id} • `}
                            {company.user_count} users • {company.order_count} orders
                        </p>
                    </div>
                </div>
            </div>

            {/* Tabs */}
            <div className="border-b mb-6">
                <div className="flex gap-6">
                    {TABS.map((tab) => {
                        const Icon = tab.icon;
                        const isActive = activeTab === tab.id;
                        return (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`flex items-center gap-2 py-3 border-b-2 transition-colors ${
                                    isActive
                                        ? 'border-blue-600 text-blue-600'
                                        : 'border-transparent text-slate-500 hover:text-slate-700'
                                }`}
                            >
                                <Icon className="h-4 w-4" />
                                <span className="text-sm font-medium">{tab.label}</span>
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Tab Content */}
            {activeTab === 'overview' && <OverviewTab company={company} onUpdate={fetchCompany} />}
            {activeTab === 'policies' && <PoliciesTab company={company} onUpdate={fetchCompany} />}
            {activeTab === 'modules' && <ModulesTab company={company} />}
            {activeTab === 'users' && <UsersTab company={company} />}
            {activeTab === 'catalogue' && <CatalogueTab company={company} />}
            {activeTab === 'pricing' && <PricingTab company={company} />}
            {activeTab === 'invoices' && <InvoicesCreditTab company={company} onUpdate={fetchCompany} />}
            {activeTab === 'inbound' && <InboundTab company={company} />}
        </div>
    );
}

// ==================== Overview Tab ====================
function OverviewTab({ company, onUpdate }) {
    const [isEditing, setIsEditing] = useState(false);
    const [isBillingEditing, setIsBillingEditing] = useState(false);
    const [editData, setEditData] = useState({});
    const [billingData, setBillingData] = useState({});
    const [isSaving, setIsSaving] = useState(false);
    const [isSyncing, setIsSyncing] = useState(false);
    const [isRecalculatingVat, setIsRecalculatingVat] = useState(false);
    const [vatRecalcResult, setVatRecalcResult] = useState(null);
    
    // e-conomic metadata for displaying meaningful labels
    const [economicMetadata, setEconomicMetadata] = useState({
        customerGroups: {},
        paymentTerms: {},
        vatZones: {}
    });
    
    // Fetch e-conomic metadata on mount
    useEffect(() => {
        const fetchEconomicMetadata = async () => {
            const token = localStorage.getItem('token');
            const headers = { Authorization: `Bearer ${token}` };
            const baseUrl = API_BASE;
            
            try {
                // Fetch all metadata in parallel
                const [groupsRes, termsRes, zonesRes] = await Promise.all([
                    axios.get(`${baseUrl}/api/economic/customer-groups`, { headers }).catch(() => ({ data: {} })),
                    axios.get(`${baseUrl}/api/economic/payment-terms`, { headers }).catch(() => ({ data: {} })),
                    axios.get(`${baseUrl}/api/economic/vat-zones`, { headers }).catch(() => ({ data: {} }))
                ]);
                
                // Build lookup maps by ID (handle snake_case response format)
                const customerGroups = {};
                (groupsRes.data?.customer_groups || groupsRes.data?.collection || []).forEach(g => {
                    const id = g.customer_group_number || g.customerGroupNumber;
                    customerGroups[id] = g.name;
                });
                
                const paymentTerms = {};
                (termsRes.data?.payment_terms || termsRes.data?.collection || []).forEach(t => {
                    const id = t.payment_terms_number || t.paymentTermsNumber;
                    paymentTerms[id] = t.name;
                });
                
                const vatZones = {};
                (zonesRes.data?.vat_zones || zonesRes.data?.collection || []).forEach(z => {
                    const id = z.vat_zone_number || z.vatZoneNumber;
                    vatZones[id] = z.name;
                });
                
                setEconomicMetadata({ customerGroups, paymentTerms, vatZones });
            } catch (error) {
                console.error('Failed to fetch e-conomic metadata:', error);
            }
        };
        
        fetchEconomicMetadata();
    }, []);
    
    // Helper to get label with fallback to ID
    const getEconomicLabel = (type, id) => {
        if (!id) return '—';
        const map = economicMetadata[type];
        const name = map?.[id];
        if (name) {
            return `${name} (#${id})`;
        }
        return String(id);
    };

    const handleEdit = () => {
        setEditData({
            name: company.name,
            external_id: company.external_id || '',
            type: company.type || 'private',
        });
        setIsEditing(true);
    };

    const handleEditBilling = () => {
        setBillingData({
            billing_address_line1: company.billing_address_line1 || company.billing_address?.address || '',
            billing_address_line2: company.billing_address_line2 || '',
            billing_postal_code: company.billing_postal_code || company.billing_address?.postal_code || '',
            billing_city: company.billing_city || company.billing_address?.city || '',
            billing_country: company.billing_country || company.billing_address?.country || company.country || '',
            billing_email: company.billing_email || '',
            country: company.country || '',
            currency: company.currency || '',
            vat_id: company.vat_id || '',
            default_vat_mode: company.default_vat_mode || 'without_vat',
            default_vat_rate: company.default_vat_rate || 0,
            payment_terms_id: company.payment_terms_id || company.economic_payment_terms || 3, // Default: Netto 14 dage
            vat_zone_id: company.economic_vat_zone || 1, // Default: Domestic
        });
        setIsBillingEditing(true);
    };

    const handleSave = async () => {
        setIsSaving(true);
        try {
            const token = localStorage.getItem('token');
            await axios.put(
                `${API_BASE}/api/admin/organizations/${company.id}`,
                { name: editData.name, type: editData.type },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            
            if (editData.external_id !== (company.external_id || '')) {
                await axios.put(
                    `${API_BASE}/api/admin/organizations/${company.id}/external-id?external_id=${encodeURIComponent(editData.external_id)}`,
                    {},
                    { headers: { Authorization: `Bearer ${token}` } }
                );
            }
            
            toast.success('Company updated');
            setIsEditing(false);
            onUpdate();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to update');
        } finally {
            setIsSaving(false);
        }
    };

    const handleSaveBilling = async () => {
        setIsSaving(true);
        try {
            const token = localStorage.getItem('token');
            await axios.put(
                `${API_BASE}/api/organizations/${company.id}/billing-profile`,
                {
                    billing_address_line1: billingData.billing_address_line1,
                    billing_address_line2: billingData.billing_address_line2,
                    billing_postal_code: billingData.billing_postal_code,
                    billing_city: billingData.billing_city,
                    billing_country: billingData.billing_country,
                    billing_email: billingData.billing_email,
                    country: billingData.country,
                    currency: billingData.currency,
                    vat_id: billingData.vat_id,
                    default_vat_mode: billingData.default_vat_mode,
                    default_vat_rate: billingData.default_vat_rate,
                    payment_terms_id: billingData.payment_terms_id,
                    vat_zone_id: billingData.vat_zone_id,
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success('Billing profile updated');
            if (billingData.vat_zone_id !== company.economic_vat_zone && company.economic_customer_number) {
                toast.info('VAT Zone synced to e-conomic');
            }
            setIsBillingEditing(false);
            onUpdate();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to update billing');
        } finally {
            setIsSaving(false);
        }
    };

    const handleSyncToEconomic = async () => {
        setIsSyncing(true);
        try {
            const token = localStorage.getItem('token');
            const endpoint = company.economic_customer_number 
                ? `/api/admin/organizations/${company.id}/sync-economic`
                : `/api/admin/organizations/${company.id}/approve-and-sync`;
            
            await axios.post(
                `${API_BASE}${endpoint}`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success(company.economic_customer_number 
                ? 'Synced with e-conomic' 
                : 'Approved and synced to e-conomic');
            onUpdate();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to sync');
        } finally {
            setIsSyncing(false);
        }
    };

    const handleRecalculateVat = async () => {
        setIsRecalculatingVat(true);
        setVatRecalcResult(null);
        try {
            const token = localStorage.getItem('token');
            const response = await axios.post(
                `${API_BASE}/api/admin/organizations/${company.id}/recalculate-vat`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setVatRecalcResult(response.data);
            toast.success(`VAT recalculated for ${response.data.orders_updated} orders`);
            onUpdate();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to recalculate VAT');
        } finally {
            setIsRecalculatingVat(false);
        }
    };

    const statusConfig = BILLING_STATUS_CONFIG[company.billing_status] || BILLING_STATUS_CONFIG.incomplete;

    // Determine e-conomic sync status
    const getEconomicStatus = () => {
        if (!company.economic_customer_number) {
            return { label: 'Not Synced', color: 'text-slate-500', bg: 'bg-slate-100' };
        }
        if (company.economic_needs_sync) {
            return { label: 'Needs Update', color: 'text-amber-600', bg: 'bg-amber-50' };
        }
        return { label: 'Synced', color: 'text-emerald-600', bg: 'bg-emerald-50' };
    };

    const economicStatus = getEconomicStatus();

    // Get missing billing fields
    const getMissingFields = () => {
        const missing = [];
        if (!company.type) missing.push('Account type');
        if (!company.billing_address_line1 && !company.billing_address?.address) missing.push('Billing address');
        if (!company.billing_postal_code && !company.billing_address?.postal_code) missing.push('Postal code');
        if (!company.billing_city && !company.billing_address?.city) missing.push('City');
        if (!company.billing_country && !company.billing_address?.country && !company.country) missing.push('Country');
        if (!company.billing_email) missing.push('Billing email');
        if (!company.currency) missing.push('Currency');
        if (company.type === 'business' && !company.vat_id) missing.push('VAT ID');
        return missing;
    };

    const missingFields = getMissingFields();

    return (
        <div className="space-y-6">
            {/* Basic Info Card */}
            <div className="border rounded-lg p-4">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="font-medium text-slate-900">Basic Information</h3>
                    {!isEditing && (
                        <Button variant="outline" size="sm" onClick={handleEdit}>
                            <Edit className="h-4 w-4 mr-2" />
                            Edit
                        </Button>
                    )}
                </div>

                {isEditing ? (
                    <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <label className="text-sm font-medium">Company Name</label>
                                <Input
                                    value={editData.name}
                                    onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="text-sm font-medium">External ID (CVR)</label>
                                <Input
                                    value={editData.external_id}
                                    onChange={(e) => setEditData({ ...editData, external_id: e.target.value })}
                                    placeholder="Optional"
                                />
                            </div>
                        </div>
                        <div className="space-y-2">
                            <label className="text-sm font-medium">Type</label>
                            <Select 
                                value={editData.type} 
                                onValueChange={(v) => setEditData({ ...editData, type: v })}
                            >
                                <SelectTrigger className="w-[200px]">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="business">Business</SelectItem>
                                    <SelectItem value="private">Private</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="flex gap-2">
                            <Button onClick={handleSave} disabled={isSaving}>
                                {isSaving ? 'Saving...' : 'Save Changes'}
                            </Button>
                            <Button variant="outline" onClick={() => setIsEditing(false)}>
                                Cancel
                            </Button>
                        </div>
                    </div>
                ) : (
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <p className="text-xs text-slate-400">Company Name</p>
                            <p className="text-sm font-medium">{company.name}</p>
                        </div>
                        <div>
                            <p className="text-xs text-slate-400">External ID (CVR)</p>
                            <p className="text-sm font-medium">{company.external_id || '—'}</p>
                        </div>
                        <div>
                            <p className="text-xs text-slate-400">Type</p>
                            <p className="text-sm font-medium capitalize">{company.type || 'private'}</p>
                        </div>
                        <div>
                            <p className="text-xs text-slate-400">Created</p>
                            <p className="text-sm font-medium">
                                {new Date(company.created_at).toLocaleDateString('en-GB')}
                            </p>
                        </div>
                    </div>
                )}
            </div>

            {/* Partner Programme Card — only shown when the org is a
                member of an ACTIVE programme. Snapshot is populated by
                GET /api/organizations/{id} (see organizations.py) so
                the discount grid lands in one round-trip.
                Deliberately read-only here — admins manage member
                removal from System Settings → Partner Registration. */}
            {company.partner_programme && (
                <div className="border rounded-lg p-4 bg-emerald-50/40 border-emerald-100"
                    data-testid="company-partner-programme-card">
                    <div className="flex items-center justify-between mb-3">
                        <h3 className="font-medium text-slate-900">Affinity Programme</h3>
                        <a href="/settings/partners?tab=affinity" target="_blank" rel="noreferrer"
                            className="text-xs text-blue-600 hover:underline">
                            Manage members →
                        </a>
                    </div>
                    <div className="flex flex-wrap items-baseline gap-x-6 gap-y-2">
                        <div>
                            <p className="text-xs text-slate-500 mb-0.5">Programme</p>
                            <p className="text-sm font-medium text-emerald-900"
                                data-testid="company-partner-programme-name">
                                {company.partner_programme.name}
                            </p>
                        </div>
                        <div>
                            <p className="text-xs text-slate-500 mb-0.5">Member discounts</p>
                            <div className="flex flex-wrap gap-1.5">
                                {Object.entries(company.partner_programme.discount_by_family || {})
                                    .filter(([, pct]) => Number(pct) > 0)
                                    .map(([family, pct]) => (
                                        <span key={family}
                                            className="px-2 py-0.5 text-xs rounded-md border bg-emerald-100 border-emerald-200 text-emerald-800">
                                            <span className="capitalize">{family}</span>{' '}
                                            <span className="font-semibold">{pct}%</span>
                                        </span>
                                    ))}
                                {Object.values(company.partner_programme.discount_by_family || {})
                                    .every((v) => !Number(v)) && (
                                        <span className="text-xs text-slate-400">
                                            No per-family discount configured
                                        </span>
                                    )}
                            </div>
                        </div>
                        {company.partner_programme.joined_at && (
                            <div>
                                <p className="text-xs text-slate-500 mb-0.5">Joined</p>
                                <p className="text-sm">
                                    {new Date(company.partner_programme.joined_at).toLocaleDateString('en-GB')}
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Billing Address Card */}
            <div className="border rounded-lg p-4">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="font-medium text-slate-900">Billing Information</h3>
                    {!isBillingEditing && (
                        <Button variant="outline" size="sm" onClick={handleEditBilling}>
                            <Edit className="h-4 w-4 mr-2" />
                            Edit
                        </Button>
                    )}
                </div>

                {isBillingEditing ? (
                    <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <label className="text-sm font-medium">Address Line 1 *</label>
                                <Input
                                    value={billingData.billing_address_line1}
                                    onChange={(e) => setBillingData({ ...billingData, billing_address_line1: e.target.value })}
                                    placeholder="Street address"
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="text-sm font-medium">Address Line 2</label>
                                <Input
                                    value={billingData.billing_address_line2}
                                    onChange={(e) => setBillingData({ ...billingData, billing_address_line2: e.target.value })}
                                    placeholder="Apt, suite, etc. (optional)"
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="text-sm font-medium">Postal Code *</label>
                                <Input
                                    value={billingData.billing_postal_code}
                                    onChange={(e) => setBillingData({ ...billingData, billing_postal_code: e.target.value })}
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="text-sm font-medium">City *</label>
                                <Input
                                    value={billingData.billing_city}
                                    onChange={(e) => setBillingData({ ...billingData, billing_city: e.target.value })}
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="text-sm font-medium">Billing Country *</label>
                                <CountrySelect
                                    value={billingData.billing_country}
                                    onChange={(countryCode) => {
                                        const isDenmark = countryCode === 'DK';
                                        setBillingData({
                                            ...billingData,
                                            billing_country: countryCode,
                                            default_vat_mode: isDenmark ? 'with_vat' : 'without_vat',
                                            default_vat_rate: isDenmark ? 25 : 0
                                        });
                                    }}
                                    testid="company-billing-country"
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="text-sm font-medium">Billing Email *</label>
                                <Input
                                    type="email"
                                    value={billingData.billing_email}
                                    onChange={(e) => setBillingData({ ...billingData, billing_email: e.target.value })}
                                    placeholder="Required for invoice delivery"
                                />
                            </div>
                        </div>
                        <div className="grid grid-cols-3 gap-4">
                            <div className="space-y-2">
                                <label className="text-sm font-medium">Country</label>
                                <CountrySelect
                                    value={billingData.country}
                                    onChange={(code) => setBillingData({ ...billingData, country: code })}
                                    testid="company-country"
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="text-sm font-medium">Currency {company.type === 'business' ? '*' : ''}</label>
                                <Select 
                                    value={billingData.currency} 
                                    onValueChange={(v) => setBillingData({ ...billingData, currency: v })}
                                >
                                    <SelectTrigger>
                                        <SelectValue placeholder="Select currency" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="DKK">DKK</SelectItem>
                                        <SelectItem value="SEK">SEK</SelectItem>
                                        <SelectItem value="NOK">NOK</SelectItem>
                                        <SelectItem value="EUR">EUR</SelectItem>
                                        <SelectItem value="USD">USD</SelectItem>
                                        <SelectItem value="GBP">GBP</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-2">
                                <label className="text-sm font-medium">VAT ID {company.type === 'business' ? '*' : ''}</label>
                                <Input
                                    value={billingData.vat_id}
                                    onChange={(e) => setBillingData({ ...billingData, vat_id: e.target.value })}
                                    placeholder="e.g., DK12345678"
                                />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4 p-3 bg-slate-50 rounded-lg">
                            <div className="space-y-2">
                                <label className="text-sm font-medium">Default VAT</label>
                                <Select 
                                    value={billingData.default_vat_mode || 'without_vat'} 
                                    onValueChange={(v) => setBillingData({ 
                                        ...billingData, 
                                        default_vat_mode: v,
                                        default_vat_rate: v === 'with_vat' ? 25 : 0
                                    })}
                                >
                                    <SelectTrigger>
                                        <SelectValue placeholder="Select VAT mode" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="with_vat">With VAT (25%)</SelectItem>
                                        <SelectItem value="without_vat">Without VAT (0%)</SelectItem>
                                    </SelectContent>
                                </Select>
                                <p className="text-xs text-slate-500">
                                    Auto-default: DK = 25%, Others = 0%
                                </p>
                            </div>
                            <div className="space-y-2">
                                <label className="text-sm font-medium">VAT Rate (%)</label>
                                <Input
                                    type="number"
                                    value={billingData.default_vat_rate || (billingData.default_vat_mode === 'with_vat' ? 25 : 0)}
                                    onChange={(e) => setBillingData({ ...billingData, default_vat_rate: parseFloat(e.target.value) || 0 })}
                                    disabled={billingData.default_vat_mode !== 'with_vat'}
                                    placeholder="25"
                                />
                            </div>
                        </div>
                        {/* Payment Terms Section */}
                        <div className="p-3 bg-blue-50 rounded-lg space-y-2">
                            <label className="text-sm font-medium flex items-center gap-2">
                                Payment Terms
                                <span className="text-xs text-slate-500 font-normal">(for invoices)</span>
                            </label>
                            <Select 
                                value={String(billingData.payment_terms_id || 3)}
                                onValueChange={(v) => setBillingData({ ...billingData, payment_terms_id: parseInt(v) })}
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder="Select payment terms" />
                                </SelectTrigger>
                                <SelectContent>
                                    {Object.keys(economicMetadata.paymentTerms || {}).length > 0 ? (
                                        Object.entries(economicMetadata.paymentTerms).map(([id, name]) => (
                                            <SelectItem key={id} value={id}>{name}</SelectItem>
                                        ))
                                    ) : (
                                        <>
                                            <SelectItem value="1">Netto kontant</SelectItem>
                                            <SelectItem value="2">Netto 8 dage</SelectItem>
                                            <SelectItem value="3">Netto 14 dage</SelectItem>
                                            <SelectItem value="4">Netto 30 dage</SelectItem>
                                            <SelectItem value="5">Netto 60 dage</SelectItem>
                                        </>
                                    )}
                                </SelectContent>
                            </Select>
                            <p className="text-xs text-slate-500">
                                Default: Netto 14 dage. Customer-specific selection will be used on future invoices.
                            </p>
                        </div>
                        {/* VAT Zone Section */}
                        <div className="p-3 bg-purple-50 rounded-lg space-y-2">
                            <label className="text-sm font-medium flex items-center gap-2">
                                VAT Zone
                                <span className="text-xs text-slate-500 font-normal">(synced with e-conomic)</span>
                            </label>
                            <Select 
                                value={String(billingData.vat_zone_id || 1)}
                                onValueChange={(v) => setBillingData({ ...billingData, vat_zone_id: parseInt(v) })}
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder="Select VAT zone" />
                                </SelectTrigger>
                                <SelectContent>
                                    {Object.keys(economicMetadata.vatZones || {}).length > 0 ? (
                                        Object.entries(economicMetadata.vatZones).map(([id, name]) => (
                                            <SelectItem key={id} value={id}>{name}</SelectItem>
                                        ))
                                    ) : (
                                        <>
                                            <SelectItem value="1">Domestic</SelectItem>
                                            <SelectItem value="2">EU</SelectItem>
                                            <SelectItem value="3">Abroad</SelectItem>
                                        </>
                                    )}
                                </SelectContent>
                            </Select>
                            <p className="text-xs text-slate-500">
                                Changes will be synced to e-conomic automatically.
                            </p>
                        </div>
                        <div className="flex gap-2">
                            <Button onClick={handleSaveBilling} disabled={isSaving}>
                                {isSaving ? 'Saving...' : 'Save Billing Info'}
                            </Button>
                            <Button variant="outline" onClick={() => setIsBillingEditing(false)}>
                                Cancel
                            </Button>
                        </div>
                    </div>
                ) : (
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <p className="text-xs text-slate-400">Address</p>
                            <p className="text-sm font-medium">
                                {company.billing_address_line1 || company.billing_address?.address || '—'}
                                {company.billing_address_line2 && `, ${company.billing_address_line2}`}
                            </p>
                        </div>
                        <div>
                            <p className="text-xs text-slate-400">City</p>
                            <p className="text-sm font-medium">
                                {company.billing_postal_code || company.billing_address?.postal_code || ''}{' '}
                                {company.billing_city || company.billing_address?.city || '—'}
                            </p>
                        </div>
                        <div>
                            <p className="text-xs text-slate-400">Country</p>
                            <p className="text-sm font-medium">
                                {company.billing_country || company.billing_address?.country || company.country || '—'}
                            </p>
                        </div>
                        <div>
                            <p className="text-xs text-slate-400">Billing Email</p>
                            <p className="text-sm font-medium">{company.billing_email || <span className="text-red-500">Required</span>}</p>
                        </div>
                        <div>
                            <p className="text-xs text-slate-400">Currency</p>
                            <p className="text-sm font-medium">{company.currency || '—'}</p>
                        </div>
                        <div>
                            <p className="text-xs text-slate-400">VAT ID</p>
                            <p className="text-sm font-medium">{company.vat_id || '—'}</p>
                        </div>
                        <div>
                            <p className="text-xs text-slate-400">Default VAT</p>
                            <p className="text-sm font-medium">
                                {company.default_vat_mode === 'with_vat' 
                                    ? `With VAT (${company.default_vat_rate || 25}%)`
                                    : company.default_vat_mode === 'without_vat'
                                        ? 'Without VAT (0%)'
                                        : '—'}
                            </p>
                        </div>
                        <div>
                            <p className="text-xs text-slate-400">Payment Terms</p>
                            <p className="text-sm font-medium">
                                {getEconomicLabel('paymentTerms', company.payment_terms_id || company.economic_payment_terms) || 'Netto 14 dage (default)'}
                            </p>
                        </div>
                        <div>
                            <p className="text-xs text-slate-400">VAT Zone</p>
                            <p className="text-sm font-medium">
                                {getEconomicLabel('vatZones', company.economic_vat_zone) || 'Domestic (default)'}
                            </p>
                        </div>
                    </div>
                )}

                {/* VAT Recalculation Action - shown in read-only mode */}
                {!isBillingEditing && company.default_vat_mode && (
                    <div className="mt-4 p-3 bg-slate-50 border border-slate-200 rounded-lg">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-slate-700">Apply VAT to Existing Orders</p>
                                <p className="text-xs text-slate-500">
                                    Recalculate all open orders to use: {company.default_vat_mode === 'with_vat' 
                                        ? `With VAT (${company.default_vat_rate || 25}%)`
                                        : 'Without VAT (0%)'}
                                </p>
                            </div>
                            <Button 
                                variant="outline" 
                                size="sm"
                                onClick={handleRecalculateVat}
                                disabled={isRecalculatingVat}
                                className="text-slate-600 hover:text-slate-900"
                                data-testid="recalculate-vat-btn"
                            >
                                {isRecalculatingVat ? (
                                    <>
                                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                        Recalculating...
                                    </>
                                ) : (
                                    'Recalculate VAT'
                                )}
                            </Button>
                        </div>
                        
                        {/* Show result after recalculation */}
                        {vatRecalcResult && (
                            <div className="mt-3 pt-3 border-t border-slate-200">
                                <p className="text-sm text-emerald-600 font-medium">
                                    ✓ {vatRecalcResult.orders_updated} orders updated
                                    {vatRecalcResult.orders_skipped > 0 && (
                                        <span className="text-slate-500 font-normal">
                                            {' '}({vatRecalcResult.orders_skipped} skipped - no sales document)
                                        </span>
                                    )}
                                </p>
                                {vatRecalcResult.details && vatRecalcResult.details.length > 0 && (
                                    <details className="mt-2">
                                        <summary className="text-xs text-slate-500 cursor-pointer hover:text-slate-700">
                                            View details
                                        </summary>
                                        <div className="mt-2 max-h-40 overflow-y-auto">
                                            {vatRecalcResult.details.map((d, i) => (
                                                <div key={i} className="text-xs py-1 border-b border-slate-100 last:border-0">
                                                    <span className="font-medium">Order #{d.order_number}:</span>{' '}
                                                    {d.status === 'updated' ? (
                                                        <span className="text-emerald-600">
                                                            {d.old_vat} → {d.new_vat} (Total: {d.total_incl_vat})
                                                        </span>
                                                    ) : (
                                                        <span className="text-slate-400">{d.reason}</span>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </details>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {/* Missing fields warning */}
                {missingFields.length > 0 && (
                    <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                        <div className="flex items-start gap-2">
                            <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5" />
                            <div>
                                <p className="text-sm font-medium text-amber-800">Missing Fields</p>
                                <p className="text-sm text-amber-600">
                                    {missingFields.join(', ')}
                                </p>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* e-conomic Integration Card */}
            <div className="border rounded-lg p-4">
                <h3 className="font-medium text-slate-900 mb-4">e-conomic Integration</h3>
                
                <div className="flex items-center gap-4 mb-4">
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${economicStatus.bg} ${economicStatus.color}`}>
                        {economicStatus.label}
                    </span>
                    <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={handleSyncToEconomic}
                        disabled={isSyncing || missingFields.length > 0}
                    >
                        {isSyncing ? 'Syncing...' : company.economic_customer_number ? 'Sync with e-conomic' : 'Approve & Sync'}
                    </Button>
                </div>

                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <p className="text-xs text-slate-400">Customer Number</p>
                        <p className="text-sm font-medium">
                            {company.economic_customer_number || '—'}
                        </p>
                    </div>
                    <div>
                        <p className="text-xs text-slate-400">Last Synced</p>
                        <p className="text-sm font-medium">
                            {company.economic_last_synced_at 
                                ? new Date(company.economic_last_synced_at).toLocaleString('en-GB')
                                : company.economic_synced_at 
                                    ? new Date(company.economic_synced_at).toLocaleString('en-GB')
                                    : '—'}
                        </p>
                    </div>
                    <div>
                        <p className="text-xs text-slate-400">Customer Group</p>
                        <p className="text-sm font-medium">{getEconomicLabel('customerGroups', company.economic_customer_group)}</p>
                    </div>
                    <div>
                        <p className="text-xs text-slate-400">Payment Terms</p>
                        <p className="text-sm font-medium">{getEconomicLabel('paymentTerms', company.economic_payment_terms)}</p>
                    </div>
                    <div>
                        <p className="text-xs text-slate-400">VAT Zone</p>
                        <p className="text-sm font-medium">{getEconomicLabel('vatZones', company.economic_vat_zone)}</p>
                    </div>
                    <div>
                        <p className="text-xs text-slate-400">Billing Status</p>
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${statusConfig.bg} ${statusConfig.color}`}>
                            {statusConfig.label}
                        </span>
                    </div>
                </div>

                {missingFields.length > 0 && (
                    <p className="text-sm text-amber-600 mt-4">
                        ⚠️ Complete billing profile to enable sync
                    </p>
                )}
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-3 gap-4">
                <div className="border rounded-lg p-4 text-center">
                    <p className="text-2xl font-bold text-slate-900">{company.user_count || 0}</p>
                    <p className="text-sm text-slate-500">Users</p>
                </div>
                <div className="border rounded-lg p-4 text-center">
                    <p className="text-2xl font-bold text-slate-900">{company.order_count || 0}</p>
                    <p className="text-sm text-slate-500">Orders</p>
                </div>
                <div className="border rounded-lg p-4 text-center">
                    <p className="text-2xl font-bold text-slate-900">
                        {new Date(company.created_at).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })}
                    </p>
                    <p className="text-sm text-slate-500">Created</p>
                </div>
            </div>
        </div>
    );
}

// ==================== Modules Tab ====================
// Admin-only panel to toggle which Flow modules are enabled for a
// company. Reads/writes GET|PATCH /api/companies/{id}/modules.
// Production is always on and rendered read-only; the other modules
// (Retail, Inventory) are placeholder entitlements for future
// products — toggling them NOW just grants access; the actual
// product UI/backend will be delivered separately. See
// backend/services/modules.py for the registry.
function ModulesTab({ company }) {
    const [data, setData] = useState(null);
    const [enabled, setEnabled] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [dirty, setDirty] = useState(false);

    useEffect(() => {
        let mounted = true;
        setIsLoading(true);
        axios.get(`${API_BASE}/api/companies/${company.id}/modules`)
            .then((r) => {
                if (!mounted) return;
                setData(r.data);
                setEnabled(r.data.modules || []);
            })
            .catch(() => mounted && toast.error('Could not load modules'))
            .finally(() => mounted && setIsLoading(false));
        return () => { mounted = false; };
    }, [company.id]);

    const toggle = (key, item) => {
        if (item.always_on) return;
        setDirty(true);
        setEnabled((prev) =>
            prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
        );
    };

    const save = async () => {
        setIsSaving(true);
        try {
            // Server forces ``production`` back on via normalise, but
            // sending it explicitly makes the UI → wire mapping
            // obvious in logs.
            const r = await axios.patch(
                `${API_BASE}/api/companies/${company.id}/modules`,
                { modules: enabled },
            );
            setData(r.data);
            setEnabled(r.data.modules || []);
            setDirty(false);
            toast.success('Modules updated');
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Could not save modules');
        } finally {
            setIsSaving(false);
        }
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-12 text-slate-500">
                <Loader2 className="h-5 w-5 animate-spin mr-2" />Loading modules…
            </div>
        );
    }

    const catalog = data?.catalog || [];

    return (
        <div className="space-y-5 max-w-3xl" data-testid="modules-tab">
            <div>
                <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                    <Layers className="h-5 w-5 text-slate-600" />
                    Flow modules
                </h2>
                <p className="text-sm text-slate-500 mt-1">
                    Enable additional Flow modules for this company. Production is
                    always on. Retail and Inventory are reserved slots for upcoming
                    products — enabling them now simply grants this company access
                    once those products ship.
                </p>
            </div>

            <div className="space-y-3">
                {catalog.map((item) => {
                    const checked = enabled.includes(item.key);
                    const isComingSoon = item.status === 'coming_soon';
                    return (
                        <div
                            key={item.key}
                            className={`flex items-start gap-4 rounded-lg border px-4 py-3 ${
                                checked ? 'border-blue-200 bg-blue-50/40' : 'border-slate-200 bg-white'
                            }`}
                            data-testid={`module-row-${item.key}`}
                        >
                            <label className="flex items-start gap-3 flex-1 cursor-pointer">
                                <input
                                    type="checkbox"
                                    className="mt-1 h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                                    checked={checked}
                                    disabled={item.always_on}
                                    onChange={() => toggle(item.key, item)}
                                    data-testid={`module-toggle-${item.key}`}
                                />
                                <div className="flex-1">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="font-medium text-slate-900">{item.name}</span>
                                        {item.always_on && (
                                            <span className="text-[10px] uppercase tracking-wide font-semibold bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">
                                                Always on
                                            </span>
                                        )}
                                        {isComingSoon && (
                                            <span className="text-[10px] uppercase tracking-wide font-semibold bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">
                                                Coming soon
                                            </span>
                                        )}
                                    </div>
                                    <p className="text-xs text-slate-500 mt-1">{item.description}</p>
                                </div>
                            </label>
                        </div>
                    );
                })}
            </div>

            <div className="pt-3 flex items-center justify-end gap-3 border-t border-slate-100">
                {dirty && (
                    <span className="text-xs text-amber-700">You have unsaved changes.</span>
                )}
                <Button
                    onClick={save}
                    disabled={!dirty || isSaving}
                    data-testid="modules-save-btn"
                >
                    {isSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                    Save
                </Button>
            </div>
        </div>
    );
}

// ==================== Policies Tab ====================
function PoliciesTab({ company, onUpdate }) {
    const [policies, setPolicies] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [catalogs, setCatalogs] = useState([]);
    
    // Order Types state
    const [orderTypesData, setOrderTypesData] = useState(null);
    const [isSavingOrderTypes, setIsSavingOrderTypes] = useState(false);

    // Demo Company state — persisted on the organization document, not
    // inside policies. Toggled via a dedicated admin endpoint.
    const [isDemo, setIsDemo] = useState(Boolean(company?.is_demo));
    const [isSavingDemo, setIsSavingDemo] = useState(false);

    useEffect(() => {
        setIsDemo(Boolean(company?.is_demo));
    }, [company?.id, company?.is_demo]);

    const toggleDemoMode = async (checked) => {
        setIsSavingDemo(true);
        try {
            const token = localStorage.getItem('token');
            await axios.patch(
                `${API_BASE}/api/admin/organizations/${company.id}/demo-mode`,
                { is_demo: checked },
                { headers: { Authorization: `Bearer ${token}` } },
            );
            setIsDemo(checked);
            toast.success(
                checked
                    ? 'Demo mode enabled — new orders will be flagged and partner emails redirected'
                    : 'Demo mode disabled',
            );
            onUpdate && onUpdate();
        } catch (error) {
            toast.error('Failed to update demo mode');
        } finally {
            setIsSavingDemo(false);
        }
    };

    useEffect(() => {
        fetchPolicies();
        fetchCatalogs();
        fetchOrderTypes();
    }, [company.id]);

    const fetchPolicies = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(
                `${API_BASE}/api/organizations/${company.id}/policies`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setPolicies(response.data.policies);
        } catch (error) {
            toast.error('Failed to load policies');
        } finally {
            setIsLoading(false);
        }
    };

    const fetchCatalogs = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(
                `${API_BASE}/api/catalogs`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setCatalogs(response.data || []);
        } catch (error) {
            // Catalogs may not exist
        }
    };
    
    const fetchOrderTypes = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(
                `${API_BASE}/api/organizations/${company.id}/order-types`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setOrderTypesData(response.data);
        } catch (error) {
            console.error('Failed to load order types');
        }
    };

    const updatePolicy = async (key, value) => {
        setIsSaving(true);
        try {
            const token = localStorage.getItem('token');
            await axios.put(
                `${API_BASE}/api/organizations/${company.id}/policies`,
                { [key]: value },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setPolicies({ ...policies, [key]: value });
            toast.success('Policy updated');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to update policy');
        } finally {
            setIsSaving(false);
        }
    };
    
    const toggleOrderType = async (typeId, enabled) => {
        setIsSavingOrderTypes(true);
        try {
            const currentTypes = orderTypesData?.enabled_order_types || ['vinyl'];
            let newTypes;
            
            if (enabled) {
                newTypes = [...currentTypes, typeId];
            } else {
                newTypes = currentTypes.filter(t => t !== typeId);
            }
            
            // Must have at least one type enabled
            if (newTypes.length === 0) {
                toast.error('At least one order type must be enabled');
                setIsSavingOrderTypes(false);
                return;
            }
            
            const token = localStorage.getItem('token');
            await axios.put(
                `${API_BASE}/api/admin/organizations/${company.id}/order-types`,
                { enabled_order_types: newTypes },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            
            setOrderTypesData({ ...orderTypesData, enabled_order_types: newTypes });
            toast.success('Order types updated');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to update order types');
        } finally {
            setIsSavingOrderTypes(false);
        }
    };

    if (isLoading) {
        return <div className="text-center py-8 text-slate-400">Loading policies...</div>;
    }

    return (
        <div className="space-y-6">
            {/* Order Types Section */}
            <div className="border rounded-lg p-4">
                <h3 className="font-medium text-slate-900 mb-3">Enabled Order Types</h3>
                <p className="text-sm text-slate-500 mb-4">
                    Select which product types this company can order. Only enabled types will appear in the &quot;New Order&quot; menu.
                </p>
                
                <div className="grid grid-cols-2 gap-3">
                    {orderTypesData?.available_order_types?.map((type) => {
                        const isEnabled = orderTypesData?.enabled_order_types?.includes(type.id);
                        return (
                            <div 
                                key={type.id}
                                className={`flex items-center justify-between p-3 rounded-lg border transition-colors ${
                                    isEnabled ? 'border-blue-200 bg-blue-50' : 'border-slate-200 bg-slate-50'
                                }`}
                            >
                                <div className="flex items-center gap-3">
                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                                        isEnabled ? 'bg-blue-100 text-blue-600' : 'bg-slate-200 text-slate-400'
                                    }`}>
                                        {type.id === 'vinyl' && '🎵'}
                                        {type.id === 'cd' && <MediaDiscIcon type="cd" size={20} />}
                                        {type.id === 'dvd' && <MediaDiscIcon type="dvd" size={20} />}
                                        {type.id === 'bluray' && <MediaDiscIcon type="bluray" size={20} />}
                                    </div>
                                    <div>
                                        <p className="font-medium text-slate-900 text-sm">{type.label}</p>
                                        <p className="text-xs text-slate-500">
                                            {type.id === 'vinyl' ? 'Full configurator' : 'Simple order form'}
                                        </p>
                                    </div>
                                </div>
                                <Switch
                                    checked={isEnabled}
                                    onCheckedChange={(checked) => toggleOrderType(type.id, checked)}
                                    disabled={isSavingOrderTypes}
                                />
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Demo Company Section — amber card so admins never mistake
                it for a normal setting. */}
            <div
                className={`rounded-lg p-4 border-2 ${
                    isDemo ? 'border-amber-400 bg-amber-50' : 'border-slate-200 bg-white'
                }`}
                data-testid="demo-mode-card"
            >
                <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-medium text-slate-900">Demo Company</h3>
                            {isDemo && (
                                <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-200 text-amber-900">
                                    Active
                                </span>
                            )}
                        </div>
                        <p className="text-sm text-slate-600 mb-1">
                            Turns this company into a full demo environment. New orders inherit a
                            <span className="font-mono mx-1 px-1 bg-slate-100 rounded">demo</span>
                            flag and:
                        </p>
                        <ul className="text-sm text-slate-600 list-disc pl-5 space-y-0.5">
                            <li>Partner/supplier emails (Sony, mastering, repro) redirect to <span className="font-mono">{company?.contact_email || company?.billing_email || "the company's contact email"}</span></li>
                            <li>Excluded from Active Order Value, Open Uninvoiced, revenue dashboards</li>
                            <li>e-conomic booking + Shopify sync run in dry-run — no real partner contacted</li>
                            <li>DEMO badge shown to admins on every operational view</li>
                            <li><strong>Existing orders are not retroactively flagged</strong> — only future orders</li>
                        </ul>
                    </div>
                    <div className="flex-shrink-0 pt-1">
                        <Switch
                            checked={isDemo}
                            onCheckedChange={toggleDemoMode}
                            disabled={isSavingDemo}
                            data-testid="demo-mode-toggle"
                        />
                    </div>
                </div>
            </div>
            
            <div className="border rounded-lg divide-y">
                {/* NCB Check Policy */}
                <div className="p-4 flex items-center justify-between">
                    <div>
                        <p className="font-medium text-slate-900">Requires NCB Check</p>
                        <p className="text-sm text-slate-500">
                            Orders require NCB clearance before production can begin
                        </p>
                    </div>
                    <Switch
                        checked={policies?.requires_ncb_check ?? true}
                        onCheckedChange={(checked) => updatePolicy('requires_ncb_check', checked)}
                        disabled={isSaving}
                    />
                </div>

                {/* Auto-approve Inbound Policy */}
                <div className="p-4 flex items-center justify-between">
                    <div>
                        <p className="font-medium text-slate-900">Auto-approve Inbound Documents</p>
                        <p className="text-sm text-slate-500">
                            Automatically approve matched inbound documents without manual review
                        </p>
                    </div>
                    <Switch
                        checked={policies?.auto_approve_inbound ?? false}
                        onCheckedChange={(checked) => updatePolicy('auto_approve_inbound', checked)}
                        disabled={isSaving}
                    />
                </div>

                {/* Show Stock Levels Policy */}
                <div className="p-4 flex items-center justify-between" data-testid="policy-show-stock-levels">
                    <div>
                        <p className="font-medium text-slate-900">Show Stock Levels</p>
                        <p className="text-sm text-slate-500">
                            Reveal stock quantity, low-stock badges and months-remaining runway on the customer&rsquo;s catalogue pages. Admins always see stock internally. Off by default.
                        </p>
                    </div>
                    <Switch
                        checked={policies?.show_stock_levels ?? false}
                        onCheckedChange={(checked) => updatePolicy('show_stock_levels', checked)}
                        disabled={isSaving}
                        data-testid="policy-show-stock-levels-switch"
                    />
                </div>

                {/* DDP Tracklist Validation Policy — CD orders only */}
                <div className="p-4 flex items-center justify-between" data-testid="policy-ddp-tracklist-validation">
                    <div>
                        <p className="font-medium text-slate-900">DDP Tracklist Validation</p>
                        <p className="text-sm text-slate-500">
                            Automatically compare uploaded DDP master track counts / titles against the printed booklet PDF on CD orders. When disabled, the card is hidden and no validation runs — auto-triggers on upload and the manual &ldquo;Re-run&rdquo; button are both skipped. On by default.
                        </p>
                    </div>
                    <Switch
                        checked={policies?.ddp_tracklist_validation_enabled ?? true}
                        onCheckedChange={(checked) => updatePolicy('ddp_tracklist_validation_enabled', checked)}
                        disabled={isSaving}
                        data-testid="policy-ddp-tracklist-validation-switch"
                    />
                </div>

                {/* Default Pricing Profile */}
                <div className="p-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="font-medium text-slate-900">Default Pricing Profile</p>
                            <p className="text-sm text-slate-500">
                                Price catalog used for this company&apos;s orders (optional)
                            </p>
                        </div>
                    </div>
                    <div className="mt-3">
                        <Select
                            value={policies?.default_pricing_profile_id || 'none'}
                            onValueChange={(v) => updatePolicy('default_pricing_profile_id', v === 'none' ? null : v)}
                            disabled={isSaving}
                        >
                            <SelectTrigger className="w-[300px]">
                                <SelectValue placeholder="Select pricing profile" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="none">No default pricing</SelectItem>
                                {catalogs.map((catalog) => (
                                    <SelectItem key={catalog.id} value={catalog.id}>
                                        {catalog.name}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </div>
            </div>

            <p className="text-xs text-slate-400">
                These policies apply to all users and orders in this company. 
                Individual user settings cannot override company policies.
            </p>

            {/* Per-company override for the three vinyl order modes. Defaults
                to "Use global settings"; toggling that off lets admins pick a
                company-specific enabled set + default mode. Shares the same
                backend endpoints and safety rules as the global panel. */}
            <VinylOrderModesPanel organizationId={company.id} />
        </div>
    );
}

// ==================== Users Tab ====================
function UsersTab({ company }) {
    const navigate = useNavigate();
    const [memberships, setMemberships] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [showAddUserDialog, setShowAddUserDialog] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [selectedRole, setSelectedRole] = useState('customer');
    const [isAdding, setIsAdding] = useState(false);
    const [removingId, setRemovingId] = useState(null);

    useEffect(() => {
        fetchMemberships();
    }, [company.id]);

    const fetchMemberships = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(
                `${API_BASE}/api/organizations/${company.id}/users`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setMemberships(response.data?.memberships || []);
        } catch (error) {
            // Fallback to old API if new one doesn't exist yet
            try {
                const token = localStorage.getItem('token');
                const response = await axios.get(
                    `${API_BASE}/api/organizations/${company.id}/users`,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                // Convert old format to membership format
                const users = response.data || [];
                setMemberships(users.map(u => ({
                    id: `legacy-${u.id}`,
                    user_id: u.id,
                    company_id: company.id,
                    role: u.role === 'customer' ? 'customer' : 'member',
                    is_primary: true,
                    user_name: u.name,
                    user_email: u.email,
                    user_role: u.role,
                    is_active: u.is_active !== false,
                    last_login: u.last_login
                })));
            } catch (err) {
                toast.error('Failed to load users');
            }
        } finally {
            setIsLoading(false);
        }
    };

    const searchUsers = async (query) => {
        if (query.length < 2) {
            setSearchResults([]);
            return;
        }
        setIsSearching(true);
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(
                `${API_BASE}/api/memberships/search/users`,
                { 
                    headers: { Authorization: `Bearer ${token}` },
                    params: { company_id: company.id, q: query, exclude_members: true }
                }
            );
            setSearchResults(response.data || []);
        } catch (error) {
            toast.error('Failed to search users');
        } finally {
            setIsSearching(false);
        }
    };

    const handleAddUsers = async () => {
        if (selectedUsers.length === 0) {
            toast.error('Please select at least one user');
            return;
        }
        setIsAdding(true);
        try {
            const token = localStorage.getItem('token');
            for (const userId of selectedUsers) {
                await axios.post(
                    `${API_BASE}/api/memberships`,
                    { user_id: userId, company_id: company.id, role: selectedRole, is_primary: false },
                    { headers: { Authorization: `Bearer ${token}` } }
                );
            }
            toast.success(`Added ${selectedUsers.length} user(s) to company`);
            setShowAddUserDialog(false);
            setSelectedUsers([]);
            setSearchQuery('');
            setSearchResults([]);
            fetchMemberships();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to add users');
        } finally {
            setIsAdding(false);
        }
    };

    const handleRemoveUser = async (membership) => {
        if (!confirm(`Remove ${membership.user_name} from ${company.name}?`)) return;
        setRemovingId(membership.id);
        try {
            const token = localStorage.getItem('token');
            await axios.delete(
                `${API_BASE}/api/memberships/${membership.id}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success(`Removed ${membership.user_name} from company`);
            fetchMemberships();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to remove user');
        } finally {
            setRemovingId(null);
        }
    };

    const handleSetPrimary = async (membership) => {
        try {
            const token = localStorage.getItem('token');
            await axios.put(
                `${API_BASE}/api/memberships/${membership.id}`,
                { is_primary: true },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success(`Set ${company.name} as primary company for ${membership.user_name}`);
            fetchMemberships();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to update membership');
        }
    };

    const getMembershipRoleBadge = (role) => {
        const config = {
            owner: { bg: 'bg-amber-50', color: 'text-amber-700' },
            admin: { bg: 'bg-purple-50', color: 'text-purple-600' },
            member: { bg: 'bg-slate-100', color: 'text-slate-600' },
            viewer: { bg: 'bg-gray-50', color: 'text-gray-500' },
            customer: { bg: 'bg-blue-50', color: 'text-blue-600' },
        };
        const c = config[role] || config.member;
        return (
            <span className={`px-2 py-0.5 rounded text-xs font-medium ${c.bg} ${c.color} capitalize`}>
                {role?.replace('_', ' ')}
            </span>
        );
    };

    const toggleUserSelection = (userId) => {
        setSelectedUsers(prev => 
            prev.includes(userId) 
                ? prev.filter(id => id !== userId)
                : [...prev, userId]
        );
    };

    if (isLoading) {
        return <div className="text-center py-8 text-slate-400">Loading users...</div>;
    }

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <p className="text-sm text-slate-500">{memberships.length} user(s) in this company</p>
                <Button 
                    onClick={() => setShowAddUserDialog(true)}
                    size="sm"
                    data-testid="add-user-to-company-btn"
                >
                    <Plus className="h-4 w-4 mr-2" />
                    Add User
                </Button>
            </div>

            <div className="border rounded-lg overflow-hidden">
                <Table>
                    <TableHeader>
                        <TableRow className="bg-background">
                            <TableHead>User</TableHead>
                            <TableHead className="w-[120px]">Company Role</TableHead>
                            <TableHead className="w-[100px]">Primary</TableHead>
                            <TableHead className="w-[100px]">Status</TableHead>
                            <TableHead className="w-[80px]"></TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {memberships.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={5} className="text-center py-8 text-slate-400">
                                    No users in this company
                                </TableCell>
                            </TableRow>
                        ) : (
                            memberships.map((membership) => (
                                <TableRow 
                                    key={membership.id} 
                                    className="hover:bg-background"
                                    data-testid={`membership-row-${membership.user_id}`}
                                >
                                    <TableCell>
                                        <div>
                                            <p className="font-medium text-slate-900">{membership.user_name}</p>
                                            <p className="text-xs text-slate-400">{membership.user_email}</p>
                                        </div>
                                    </TableCell>
                                    <TableCell>{getMembershipRoleBadge(membership.role)}</TableCell>
                                    <TableCell>
                                        {membership.is_primary ? (
                                            <span className="px-2 py-0.5 rounded text-xs font-medium bg-emerald-50 text-emerald-600">
                                                Primary
                                            </span>
                                        ) : (
                                            <button
                                                onClick={() => handleSetPrimary(membership)}
                                                className="text-xs text-slate-400 hover:text-slate-600 underline"
                                            >
                                                Set as primary
                                            </button>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {membership.is_active !== false ? (
                                            <span className="text-emerald-600 text-xs">Active</span>
                                        ) : (
                                            <span className="text-slate-400 text-xs">Inactive</span>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <button
                                            onClick={() => handleRemoveUser(membership)}
                                            disabled={removingId === membership.id}
                                            className="p-1 text-slate-400 hover:text-red-600 transition-colors disabled:opacity-50"
                                            title="Remove from company"
                                            data-testid={`remove-user-${membership.user_id}`}
                                        >
                                            {removingId === membership.id ? (
                                                <Loader2 className="h-4 w-4 animate-spin" />
                                            ) : (
                                                <X className="h-4 w-4" />
                                            )}
                                        </button>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </div>

            {/* Add User Dialog */}
            <Dialog open={showAddUserDialog} onOpenChange={setShowAddUserDialog}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Users className="h-5 w-5" />
                            Add Users to {company.name}
                        </DialogTitle>
                    </DialogHeader>
                    
                    <div className="space-y-4 py-4">
                        {/* Search Input */}
                        <div className="space-y-2">
                            <label className="text-sm font-medium">Search Users</label>
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                                <Input
                                    placeholder="Search by name or email..."
                                    value={searchQuery}
                                    onChange={(e) => {
                                        setSearchQuery(e.target.value);
                                        searchUsers(e.target.value);
                                    }}
                                    className="pl-9"
                                    data-testid="search-users-input"
                                />
                            </div>
                        </div>

                        {/* Search Results */}
                        {searchQuery.length >= 2 && (
                            <div className="max-h-48 overflow-y-auto border rounded-lg">
                                {isSearching ? (
                                    <div className="p-4 text-center text-slate-400">
                                        <Loader2 className="h-4 w-4 animate-spin inline mr-2" />
                                        Searching...
                                    </div>
                                ) : searchResults.length === 0 ? (
                                    <div className="p-4 text-center text-slate-400 text-sm">
                                        No users found
                                    </div>
                                ) : (
                                    <div className="divide-y">
                                        {searchResults.map((user) => (
                                            <label
                                                key={user.id}
                                                className="flex items-center gap-3 p-3 hover:bg-slate-50 cursor-pointer"
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={selectedUsers.includes(user.id)}
                                                    onChange={() => toggleUserSelection(user.id)}
                                                    className="rounded border-slate-300"
                                                />
                                                <div className="flex-1 min-w-0">
                                                    <p className="font-medium text-slate-900 text-sm truncate">{user.name}</p>
                                                    <p className="text-xs text-slate-400 truncate">{user.email}</p>
                                                </div>
                                                <span className="px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-600 capitalize">
                                                    {user.role}
                                                </span>
                                            </label>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Selected Users Count */}
                        {selectedUsers.length > 0 && (
                            <div className="text-sm text-slate-600">
                                {selectedUsers.length} user(s) selected
                            </div>
                        )}

                        {/* Membership Role */}
                        <div className="space-y-2">
                            <label className="text-sm font-medium">Membership Role</label>
                            <Select value={selectedRole} onValueChange={setSelectedRole}>
                                <SelectTrigger data-testid="membership-role-select">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="customer">Customer</SelectItem>
                                    <SelectItem value="member">Member</SelectItem>
                                    <SelectItem value="admin">Admin</SelectItem>
                                    <SelectItem value="owner">Owner</SelectItem>
                                </SelectContent>
                            </Select>
                            <p className="text-xs text-slate-500">
                                Role within this company (separate from system role)
                            </p>
                        </div>
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={() => {
                            setShowAddUserDialog(false);
                            setSelectedUsers([]);
                            setSearchQuery('');
                            setSearchResults([]);
                        }}>
                            Cancel
                        </Button>
                        <Button 
                            onClick={handleAddUsers}
                            disabled={selectedUsers.length === 0 || isAdding}
                            data-testid="confirm-add-users-btn"
                        >
                            {isAdding ? (
                                <>
                                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                    Adding...
                                </>
                            ) : (
                                `Add ${selectedUsers.length || ''} User${selectedUsers.length !== 1 ? 's' : ''}`
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

// ==================== Pricing Tab ====================
function PricingTab({ company }) {
    const navigate = useNavigate();

    return (
        <div className="space-y-4">
            <div className="border rounded-lg p-6 text-center">
                <DollarSign className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                <h3 className="font-medium text-slate-900 mb-2">Customer Pricing</h3>
                <p className="text-sm text-slate-500 mb-4">
                    Manage pricing rules and customer-specific pricing for {company.name}
                </p>
                <Button
                    onClick={() => navigate(`/admin/pricing/customers/${company.id}`)}
                    data-testid="company-pricing-open-btn"
                >
                    <ExternalLink className="h-4 w-4 mr-2" />
                    Open Customer Pricing
                </Button>
            </div>

            {company.policies?.default_pricing_profile_id && (
                <div className="border rounded-lg p-4 bg-blue-50">
                    <p className="text-sm text-blue-800">
                        <strong>Default Pricing Profile:</strong> This company has a default pricing profile set. 
                        Orders will use this profile for pricing calculations.
                    </p>
                </div>
            )}
        </div>
    );
}

// ==================== Catalogue Tab ====================

// Lightweight thumbnail cell — renders the resolved thumbnail_url (server
// already does the priority resolution: explicit > order artwork > null).
// Falls back to a music-disc placeholder when no image is available.
//
// Hover (admin): shows an Upload icon overlay → click to replace cover.
// If an explicit cover_image_s3_key exists, also exposes a small Trash
// button to clear it (falls back to auto-detected artwork).
function CatalogueThumbnail({ entry, companyId, onChanged }) {
    const url = entry?.thumbnail_url;
    const alt = entry?.title || entry?.catalogue_number || '';
    const [errored, setErrored] = useState(false);
    const [loaded, setLoaded] = useState(false);
    const [busy, setBusy] = useState(false);
    const fileInputRef = useRef(null);
    const hasExplicitOverride = !!entry?.cover_image_s3_key;

    // Reset load state when the underlying URL changes (e.g. cover replaced)
    useEffect(() => {
        setLoaded(false);
        setErrored(false);
    }, [url]);

    const triggerPicker = (e) => {
        e?.stopPropagation();
        fileInputRef.current?.click();
    };

    const handleFile = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = ''; // reset so picking same file again still fires
        if (!file) return;
        if (!file.type.startsWith('image/')) {
            toast.error('Please pick an image file (jpg, png, webp, gif)');
            return;
        }
        if (file.size > 10 * 1024 * 1024) {
            toast.error('Image too large — max 10 MB');
            return;
        }
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append('file', file);
            await axios.post(
                `${API_BASE}/api/admin/organizations/${companyId}/catalogue/${entry.id}/cover`,
                fd,
                { headers: { 'Content-Type': 'multipart/form-data' } }
            );
            toast.success('Cover updated');
            setErrored(false);
            onChanged?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Could not upload cover');
        } finally {
            setBusy(false);
        }
    };

    const handleClearOverride = async (e) => {
        e?.stopPropagation();
        if (!window.confirm('Remove explicit cover and fall back to auto-detected artwork?')) return;
        setBusy(true);
        try {
            await axios.delete(
                `${API_BASE}/api/admin/organizations/${companyId}/catalogue/${entry.id}/cover`
            );
            toast.success('Cover override removed');
            onChanged?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Could not remove cover');
        } finally {
            setBusy(false);
        }
    };

    const showImage = url && !errored;
    return (
        <div className="relative group h-8 w-8" onClick={(e) => e.stopPropagation()}>
            <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                className="hidden"
                onChange={handleFile}
                data-testid={`catalogue-thumb-input-${entry?.id || 'x'}`}
            />
            {showImage ? (
                // Skeleton-pulse while the thumb loads; image fades in once
                // ready. Same UX as the customer Catalogue Library.
                <div className={`relative h-8 w-8 rounded-md overflow-hidden border border-slate-200 bg-slate-100 ${loaded ? '' : 'animate-pulse'}`}>
                    <img
                        src={url}
                        alt={alt}
                        loading="lazy"
                        decoding="async"
                        onLoad={() => setLoaded(true)}
                        onError={() => setErrored(true)}
                        className={`h-full w-full object-cover transition-opacity duration-150 ${loaded ? 'opacity-100' : 'opacity-0'}`}
                        data-testid="catalogue-thumb"
                    />
                </div>
            ) : (
                <div
                    className="h-8 w-8 rounded-md bg-slate-100 border border-slate-200 flex items-center justify-center"
                    data-testid="catalogue-thumb-placeholder"
                    title="No artwork yet"
                >
                    <Library className="h-3.5 w-3.5 text-slate-300" />
                </div>
            )}

            {/* Hover overlay — admin upload */}
            <button
                type="button"
                onClick={triggerPicker}
                disabled={busy}
                title={showImage ? 'Replace cover' : 'Upload cover'}
                className="absolute inset-0 rounded-md bg-black/60 text-white opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center disabled:opacity-100"
                data-testid={`catalogue-thumb-upload-${entry?.id || 'x'}`}
            >
                {busy
                    ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    : <Upload className="h-3.5 w-3.5" />}
            </button>

            {/* Tiny "X" badge for explicit overrides */}
            {hasExplicitOverride && !busy && (
                <button
                    type="button"
                    onClick={handleClearOverride}
                    title="Remove cover override (use auto-detected artwork)"
                    className="absolute -top-1.5 -right-1.5 h-4 w-4 rounded-full bg-white border border-slate-300 shadow-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-50"
                    data-testid={`catalogue-thumb-clear-${entry?.id || 'x'}`}
                >
                    <X className="h-2.5 w-2.5 text-slate-500" />
                </button>
            )}
        </div>
    );
}

function CatalogueTab({ company }) {
    // Format catalog keyed by media_type. Vinyl is intentionally numeric-only
    // (legacy "7"/"10"/"12" persisted values that match Customer-facing filters);
    // CD/DVD/Blu-ray use human-readable codes per OWNTUNE catalog spec.
    const MEDIA_FORMAT_OPTIONS = {
        vinyl:  [
            { value: '7',  label: '7" Single' },
            { value: '10', label: '10" EP' },
            { value: '12', label: '12" LP' },
        ],
        cd:     [
            { value: 'CD Single', label: 'CD Single' },
            { value: 'CD EP',     label: 'CD EP' },
            { value: 'CD Album',  label: 'CD Album' },
            { value: '2CD',       label: '2CD' },
        ],
        dvd:    [
            { value: 'DVD5', label: 'DVD5' },
            { value: 'DVD9', label: 'DVD9' },
            { value: '2DVD', label: '2DVD' },
        ],
        blu_ray:[
            { value: 'BD25', label: 'BD25' },
            { value: 'BD50', label: 'BD50' },
            { value: '2BD',  label: '2BD' },
        ],
    };
    const getFormatOptions = (mediaType) =>
        MEDIA_FORMAT_OPTIONS[(mediaType || '').toLowerCase()] || [];
    // When media_type changes, reset format if it isn't valid for the new type.
    const handleMediaTypeChange = (v) => {
        setFormData((prev) => {
            const allowed = getFormatOptions(v).map((o) => o.value);
            const keepFormat = prev.format && allowed.includes(prev.format);
            return { ...prev, media_type: v, format: keepFormat ? prev.format : '' };
        });
    };

    const [entries, setEntries] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [formatFilter, setFormatFilter] = useState('all');
    
    // Modal states
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showImportModal, setShowImportModal] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
    
    // Detail drawer
    
    
    // Form states
    const [formData, setFormData] = useState({
        catalogue_number: '',
        title: '',
        artist: '',
        media_type: 'vinyl',
        format: '',
        sony_master_ref: ''
    });
    const [editingEntry, setEditingEntry] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    
    // Import states
    const [importFile, setImportFile] = useState(null);
    const importFileInputRef = useRef(null);
    const [importPreview, setImportPreview] = useState(null);
    const [duplicateBehavior, setDuplicateBehavior] = useState('update');
    const [skuLinkBehavior, setSkuLinkBehavior] = useState('replace');
    const [isImporting, setIsImporting] = useState(false);
    const [importResult, setImportResult] = useState(null);
    const [isValidating, setIsValidating] = useState(false);
    const [validationReport, setValidationReport] = useState(null);
    const [mediaTypeFilter, setMediaTypeFilter] = useState('all');
    // Backfill state (sync legacy completed orders → catalogue entries)
    const [isBackfilling, setIsBackfilling] = useState(false);

    const handleBackfillFromCompletedOrders = async () => {
        // 1. Always run a dry-run first so admin can see what will happen.
        setIsBackfilling(true);
        try {
            const preview = await axios.post(
                `${API_BASE}/api/admin/catalogue/backfill-from-completed-orders`,
                null,
                {
                    params: {
                        dry_run: true,
                        organization_id: company.id,
                    },
                }
            );
            const p = preview.data;
            if (p.created === 0) {
                toast.info(
                    `Nothing to backfill — all ${p.scanned} completed orders already have catalogue entries.`
                );
                return;
            }
            const titles = (p.created_examples || [])
                .slice(0, 5)
                .map((e) => `#${e.order_number} ${e.catalogue_number}`)
                .join(', ');
            const more = p.created > 5 ? ` … (+${p.created - 5} more)` : '';
            const ok = window.confirm(
                `Will create ${p.created} new catalogue entries from completed orders.\n\n` +
                `Examples: ${titles}${more}\n\n` +
                `(${p.skipped_already_exists} orders already have catalogue entries and will be skipped.)\n\n` +
                `Proceed?`
            );
            if (!ok) return;

            // 2. Real run, idempotent.
            const real = await axios.post(
                `${API_BASE}/api/admin/catalogue/backfill-from-completed-orders`,
                null,
                {
                    params: {
                        dry_run: false,
                        organization_id: company.id,
                    },
                }
            );
            toast.success(
                `Created ${real.data.created} new catalogue entries from completed orders.`
            );
            fetchEntries();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Backfill failed');
        } finally {
            setIsBackfilling(false);
        }
    };

    useEffect(() => {
        fetchEntries();
    }, [company.id]);

    const fetchEntries = async () => {
        setIsLoading(true);
        try {
            const token = localStorage.getItem('token');
            const params = new URLSearchParams();
            if (searchQuery) params.append('search', searchQuery);
            if (formatFilter && formatFilter !== 'all') params.append('format_filter', formatFilter);
            if (mediaTypeFilter && mediaTypeFilter !== 'all') params.append('media_type_filter', mediaTypeFilter);
            
            const response = await axios.get(
                `${API_BASE}/api/admin/organizations/${company.id}/catalogue?${params}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setEntries(response.data || []);
        } catch (error) {
            toast.error('Failed to load catalogue entries');
        } finally {
            setIsLoading(false);
        }
    };

    // Debounced search
    useEffect(() => {
        const timer = setTimeout(() => {
            fetchEntries();
        }, 300);
        return () => clearTimeout(timer);
    }, [searchQuery, formatFilter, mediaTypeFilter]);

    const handleCreate = async () => {
        if (!formData.catalogue_number.trim()) {
            toast.error('Catalogue number is required');
            return;
        }
        setIsSaving(true);
        try {
            const token = localStorage.getItem('token');
            await axios.post(
                `${API_BASE}/api/admin/organizations/${company.id}/catalogue`,
                {
                    catalogue_number: formData.catalogue_number,
                    title: formData.title || null,
                    artist: formData.artist || null,
                    media_type: formData.media_type,
                    format: formData.format || null,
                    sony_master_ref: formData.sony_master_ref || null
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success('Catalogue entry created');
            setShowCreateModal(false);
            resetForm();
            fetchEntries();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to create entry');
        } finally {
            setIsSaving(false);
        }
    };

    const handleEdit = (entry) => {
        setEditingEntry(entry);
        setFormData({
            catalogue_number: entry.catalogue_number || '',
            title: entry.title || '',
            artist: entry.artist || '',
            media_type: (entry.media_type || 'vinyl').toLowerCase(),
            format: entry.format || '',
            sony_master_ref: entry.sony_master_ref || ''
        });
        setShowEditModal(true);
    };

    const handleUpdate = async () => {
        if (!formData.catalogue_number.trim()) {
            toast.error('Catalogue number is required');
            return;
        }
        setIsSaving(true);
        try {
            const token = localStorage.getItem('token');
            await axios.put(
                `${API_BASE}/api/admin/organizations/${company.id}/catalogue/${editingEntry.id}`,
                {
                    catalogue_number: formData.catalogue_number,
                    title: formData.title || null,
                    artist: formData.artist || null,
                    media_type: formData.media_type || null,
                    format: formData.format || null,
                    sony_master_ref: formData.sony_master_ref || null
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success('Catalogue entry updated');
            setShowEditModal(false);
            resetForm();
            fetchEntries();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to update entry');
        } finally {
            setIsSaving(false);
        }
    };

    // Detail drawer functions
    const openDetail = (entry) => {
        window.location.href = `/admin/companies/${company.id}/catalogue/${entry.id}`;
    };

    const handleRemoveSku = null; // Managed on full page now
    const handleSetPrimary = null; // Managed on full page now

    const handleDelete = async (entryId) => {
        setIsDeleting(true);
        try {
            const token = localStorage.getItem('token');
            await axios.delete(
                `${API_BASE}/api/admin/organizations/${company.id}/catalogue/${entryId}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success('Catalogue entry deleted');
            setShowDeleteConfirm(null);
            fetchEntries();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to delete entry');
        } finally {
            setIsDeleting(false);
        }
    };

    const handleExport = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(
                `${API_BASE}/api/admin/organizations/${company.id}/catalogue/export`,
                { 
                    headers: { Authorization: `Bearer ${token}` },
                    responseType: 'blob'
                }
            );
            
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `catalogue_${company.name.toLowerCase().replace(/\s+/g, '_')}.csv`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            
            toast.success('Catalogue exported');
        } catch (error) {
            toast.error('Failed to export catalogue');
        }
    };

    const handleFileSelect = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        
        const filename = file.name.toLowerCase();
        if (!filename.endsWith('.csv') && !filename.endsWith('.xlsx') && !filename.endsWith('.xls')) {
            toast.error('Only CSV and XLSX files are supported');
            return;
        }
        
        setImportFile(file);
        setImportResult(null);
        setValidationReport(null);
        
        // Auto-validate
        setIsValidating(true);
        try {
            const token = localStorage.getItem('token');
            const fd = new FormData();
            fd.append('file', file);
            const response = await axios.post(
                `${API_BASE}/api/admin/organizations/${company.id}/catalogue/import/validate`,
                fd,
                { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
            );
            setValidationReport(response.data);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Validation failed');
        } finally {
            setIsValidating(false);
        }
    };

    const handleImport = async () => {
        if (!importFile) {
            toast.error('Please select a file');
            return;
        }
        
        setIsImporting(true);
        try {
            const token = localStorage.getItem('token');
            const formData = new FormData();
            formData.append('file', importFile);
            
            const response = await axios.post(
                `${API_BASE}/api/admin/organizations/${company.id}/catalogue/import?duplicate_behavior=${duplicateBehavior}&sku_link_behavior=${skuLinkBehavior}`,
                formData,
                { 
                    headers: { 
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'multipart/form-data'
                    } 
                }
            );
            
            setImportResult(response.data);
            
            if (response.data.created > 0 || response.data.updated > 0) {
                toast.success(`Import complete: ${response.data.created} created, ${response.data.updated} updated`);
                fetchEntries();
            } else if (response.data.errors?.length > 0) {
                toast.error('Import completed with errors');
            } else {
                toast.info('No new entries imported');
            }
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Import failed');
        } finally {
            setIsImporting(false);
        }
    };

    const resetForm = () => {
        setFormData({
            catalogue_number: '',
            title: '',
            artist: '',
            media_type: 'vinyl',
            format: '',
            sony_master_ref: ''
        });
        setEditingEntry(null);
    };

    const resetImport = () => {
        setImportFile(null);
        setImportPreview(null);
        setImportResult(null);
        setValidationReport(null);
        setDuplicateBehavior('update');
        setSkuLinkBehavior('replace');
        if (importFileInputRef.current) importFileInputRef.current.value = '';
    };

    const getFormatBadge = (format) => {
        if (!format) return null;
        // Vinyl formats are stored as numeric strings ("7"/"10"/"12") and the
        // `"` inches symbol is appended on render. All other media types
        // (CD/DVD/Blu-ray) store full human-readable labels ("CD Single",
        // "DVD9", "BD25") and MUST NOT get the inches suffix.
        const isVinylInchFormat = /^\d+$/.test(format);
        const colors = {
            // Vinyl
            '7': 'bg-blue-50 text-blue-700',
            '10': 'bg-purple-50 text-purple-700',
            '12': 'bg-emerald-50 text-emerald-700',
            // CD family
            'CD Single': 'bg-sky-50 text-sky-700',
            'CD EP': 'bg-sky-50 text-sky-700',
            'CD Album': 'bg-sky-50 text-sky-700',
            '2CD': 'bg-sky-50 text-sky-700',
            // DVD family
            'DVD5': 'bg-amber-50 text-amber-700',
            'DVD9': 'bg-amber-50 text-amber-700',
            '2DVD': 'bg-amber-50 text-amber-700',
            // Blu-ray family
            'BD25': 'bg-indigo-50 text-indigo-700',
            'BD50': 'bg-indigo-50 text-indigo-700',
            '2BD': 'bg-indigo-50 text-indigo-700',
        };
        return (
            <span className={`px-2 py-0.5 rounded text-xs font-medium ${colors[format] || 'bg-slate-100 text-slate-600'}`}>
                {format}{isVinylInchFormat ? '"' : ''}
            </span>
        );
    };

    const getMediaTypeBadge = (mt) => {
        if (!mt) return <span className="text-xs text-slate-400">—</span>;
        const cfg = {
            vinyl: 'bg-amber-50 text-amber-700',
            cd: 'bg-sky-50 text-sky-700',
            dvd: 'bg-violet-50 text-violet-700',
            blu_ray: 'bg-indigo-50 text-indigo-700',
        };
        const labels = { vinyl: 'Vinyl', cd: 'CD', dvd: 'DVD', blu_ray: 'Blu-ray' };
        const key = mt.toLowerCase();
        return (
            <span className={`px-2 py-0.5 rounded text-xs font-medium ${cfg[key] || 'bg-slate-100 text-slate-600'}`}>
                {labels[key] || mt}
            </span>
        );
    };

    const filteredEntries = entries;  // Filtering done server-side

    return (
        <div className="space-y-4">
            {/* Header Actions */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <Input
                            placeholder="Search catalogue..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="pl-9 w-[280px]"
                        />
                    </div>
                    <Select value={formatFilter} onValueChange={setFormatFilter}>
                        <SelectTrigger className="w-[150px]">
                            <SelectValue placeholder="Format" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All Formats</SelectItem>
                            {mediaTypeFilter !== 'all' ? (
                                // Scope to the selected media type — same source
                                // of truth as the Edit Catalogue Entry form.
                                (MEDIA_FORMAT_OPTIONS[mediaTypeFilter] || []).map((opt) => (
                                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                                ))
                            ) : (
                                // "All Types" → expose every format, grouped by
                                // media type so the user can see what belongs
                                // where. Disabled section headers keep parity
                                // with the OWNTUNE admin design system.
                                Object.entries(MEDIA_FORMAT_OPTIONS).flatMap(([mt, opts]) => [
                                    <div key={`hdr-${mt}`} className="px-2 py-1 text-[10px] uppercase tracking-wide text-slate-400 font-semibold">
                                        {{vinyl: 'Vinyl', cd: 'CD', dvd: 'DVD', blu_ray: 'Blu-ray'}[mt]}
                                    </div>,
                                    ...opts.map((opt) => (
                                        <SelectItem key={`${mt}-${opt.value}`} value={opt.value}>{opt.label}</SelectItem>
                                    )),
                                ])
                            )}
                        </SelectContent>
                    </Select>
                    <Select value={mediaTypeFilter} onValueChange={(v) => {
                        // If the new media-type doesn't include the current
                        // formatFilter, reset it so we don't end up filtering
                        // by an invisible value.
                        const stillValid = v === 'all' || formatFilter === 'all' ||
                            (MEDIA_FORMAT_OPTIONS[v] || []).some((o) => o.value === formatFilter);
                        if (!stillValid) setFormatFilter('all');
                        setMediaTypeFilter(v);
                    }}>
                        <SelectTrigger className="w-[130px]">
                            <SelectValue placeholder="Media Type" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All Types</SelectItem>
                            <SelectItem value="vinyl">Vinyl</SelectItem>
                            <SelectItem value="cd">CD</SelectItem>
                            <SelectItem value="dvd">DVD</SelectItem>
                            <SelectItem value="blu_ray">Blu-ray</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
                <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={handleBackfillFromCompletedOrders} disabled={isBackfilling}>
                        <RefreshCw className={`h-4 w-4 mr-2 ${isBackfilling ? 'animate-spin' : ''}`} />
                        {isBackfilling ? 'Backfilling...' : 'Sync from Completed Orders'}
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => setShowImportModal(true)}>
                        <Upload className="h-4 w-4 mr-2" />
                        Import
                    </Button>
                    <Button variant="outline" size="sm" onClick={handleExport} disabled={entries.length === 0}>
                        <Download className="h-4 w-4 mr-2" />
                        Export
                    </Button>
                    <Button size="sm" onClick={() => { resetForm(); setShowCreateModal(true); }}>
                        <Plus className="h-4 w-4 mr-2" />
                        Add Entry
                    </Button>
                </div>
            </div>

            {/* Stats */}
            <div className="flex items-center gap-4 text-sm text-slate-500">
                <span>{entries.length} catalogue entries</span>
                {entries.filter(e => e.format === '12').length > 0 && (
                    <span className="text-emerald-600">{entries.filter(e => e.format === '12').length} LPs</span>
                )}
                {entries.filter(e => e.format === '7').length > 0 && (
                    <span className="text-blue-600">{entries.filter(e => e.format === '7').length} Singles</span>
                )}
                {entries.filter(e => e.sony_master_ref).length > 0 && (
                    <span className="text-purple-600">{entries.filter(e => e.sony_master_ref).length} with Sony master</span>
                )}
            </div>

            {/* Table */}
            <div className="border rounded-lg overflow-hidden">
                <Table>
                    <TableHeader>
                        <TableRow className="bg-background">
                            <TableHead className="w-[56px]"></TableHead>
                            <TableHead>Catalogue Number</TableHead>
                            <TableHead>Title</TableHead>
                            <TableHead>Artist</TableHead>
                            <TableHead className="w-[80px]">Type</TableHead>
                            <TableHead className="w-[80px]">Format</TableHead>
                            <TableHead className="w-[80px]">Source</TableHead>
                            <TableHead className="w-[80px]">SKU</TableHead>
                            <TableHead className="w-[120px]">Last Updated</TableHead>
                            <TableHead className="w-[80px]"></TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {isLoading ? (
                            <TableRow>
                                <TableCell colSpan={10} className="text-center py-8 text-slate-400">
                                    <Loader2 className="h-5 w-5 animate-spin mx-auto mb-2" />
                                    Loading catalogue...
                                </TableCell>
                            </TableRow>
                        ) : filteredEntries.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={10} className="text-center py-8">
                                    <Library className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                                    <p className="text-slate-500">No catalogue entries found</p>
                                    <p className="text-sm text-slate-400 mt-1">
                                        {searchQuery ? 'Try a different search term' : 'Import or add entries to get started'}
                                    </p>
                                </TableCell>
                            </TableRow>
                        ) : (
                            filteredEntries.map((entry) => (
                                <TableRow key={entry.id} className="hover:bg-background cursor-pointer" onClick={() => openDetail(entry)}>
                                    <TableCell>
                                        <CatalogueThumbnail
                                            entry={entry}
                                            companyId={company.id}
                                            onChanged={fetchEntries}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <span className="font-mono text-sm font-medium">{entry.catalogue_number}</span>
                                    </TableCell>
                                    <TableCell className="text-slate-700">{entry.title || '—'}</TableCell>
                                    <TableCell className="text-slate-600">{entry.artist || '—'}</TableCell>
                                    <TableCell>{getMediaTypeBadge(entry.media_type)}</TableCell>
                                    <TableCell>{getFormatBadge(entry.format) || '—'}</TableCell>
                                    <TableCell>
                                        {entry.source === 'import' ? (
                                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-50 text-blue-600">Imported</span>
                                        ) : entry.source === 'integration' ? (
                                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-purple-50 text-purple-600">Integration</span>
                                        ) : (
                                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-slate-100 text-slate-500">Owntune</span>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {(() => {
                                            const count = (entry.sku_links || []).length;
                                            if (count === 0) return <span className="text-xs text-slate-400">No SKU</span>;
                                            return (
                                                <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600">
                                                    <Link2 className="h-3 w-3" /> {count} SKU{count > 1 ? 's' : ''}
                                                </span>
                                            );
                                        })()}
                                    </TableCell>
                                    <TableCell className="text-xs text-slate-500">
                                        {new Date(entry.updated_at).toLocaleDateString('en-GB')}
                                    </TableCell>
                                    <TableCell>
                                        <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                                            <Button 
                                                variant="ghost" 
                                                size="sm" 
                                                className="h-8 w-8 p-0"
                                                onClick={() => handleEdit(entry)}
                                            >
                                                <Edit className="h-4 w-4 text-slate-400" />
                                            </Button>
                                            <Button 
                                                variant="ghost" 
                                                size="sm" 
                                                className="h-8 w-8 p-0"
                                                onClick={() => setShowDeleteConfirm(entry)}
                                            >
                                                <Trash2 className="h-4 w-4 text-slate-400 hover:text-red-500" />
                                            </Button>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </div>

            {/* Create Modal */}
            <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle>Add Catalogue Entry</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <label className="text-sm font-medium">Catalogue Number *</label>
                            <Input
                                value={formData.catalogue_number}
                                onChange={(e) => setFormData({ ...formData, catalogue_number: e.target.value })}
                                placeholder="e.g., CAT-001, ABC-12345"
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <label className="text-sm font-medium">Title</label>
                                <Input
                                    value={formData.title}
                                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                    placeholder="Album/release title"
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="text-sm font-medium">Artist</label>
                                <Input
                                    value={formData.artist}
                                    onChange={(e) => setFormData({ ...formData, artist: e.target.value })}
                                    placeholder="Artist name"
                                />
                            </div>
                        </div>
                        <div className="grid grid-cols-3 gap-4">
                            <div className="space-y-2">
                                <label className="text-sm font-medium">Media Type *</label>
                                <Select 
                                    value={formData.media_type} 
                                    onValueChange={handleMediaTypeChange}
                                >
                                    <SelectTrigger>
                                        <SelectValue placeholder="Select type" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="vinyl">Vinyl</SelectItem>
                                        <SelectItem value="cd">CD</SelectItem>
                                        <SelectItem value="dvd">DVD</SelectItem>
                                        <SelectItem value="blu_ray">Blu-ray</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-2">
                                <label className="text-sm font-medium">Format</label>
                                <Select 
                                    value={formData.format || 'none'} 
                                    onValueChange={(v) => setFormData({ ...formData, format: v === 'none' ? '' : v })}
                                >
                                    <SelectTrigger>
                                        <SelectValue placeholder="Select format" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="none">Not specified</SelectItem>
                                        {getFormatOptions(formData.media_type).map((opt) => (
                                            <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-2">
                                <label className="text-sm font-medium">Sony Master Ref</label>
                                <Input
                                    value={formData.sony_master_ref}
                                    onChange={(e) => setFormData({ ...formData, sony_master_ref: e.target.value })}
                                    placeholder="LEOS reference (optional)"
                                />
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowCreateModal(false)}>
                            Cancel
                        </Button>
                        <Button onClick={handleCreate} disabled={isSaving}>
                            {isSaving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Creating...</> : 'Create Entry'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Edit Modal */}
            <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle>Edit Catalogue Entry</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <label className="text-sm font-medium">Catalogue Number *</label>
                            <Input
                                value={formData.catalogue_number}
                                onChange={(e) => setFormData({ ...formData, catalogue_number: e.target.value })}
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <label className="text-sm font-medium">Title</label>
                                <Input
                                    value={formData.title}
                                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                />
                            </div>
                            <div className="space-y-2">
                                <label className="text-sm font-medium">Artist</label>
                                <Input
                                    value={formData.artist}
                                    onChange={(e) => setFormData({ ...formData, artist: e.target.value })}
                                />
                            </div>
                        </div>
                        <div className="grid grid-cols-3 gap-4">
                            <div className="space-y-2">
                                <label className="text-sm font-medium">Media Type *</label>
                                <Select 
                                    value={formData.media_type || 'vinyl'} 
                                    onValueChange={handleMediaTypeChange}
                                >
                                    <SelectTrigger>
                                        <SelectValue placeholder="Select type" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="vinyl">Vinyl</SelectItem>
                                        <SelectItem value="cd">CD</SelectItem>
                                        <SelectItem value="dvd">DVD</SelectItem>
                                        <SelectItem value="blu_ray">Blu-ray</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-2">
                                <label className="text-sm font-medium">Format</label>
                                <Select 
                                    value={formData.format || 'none'} 
                                    onValueChange={(v) => setFormData({ ...formData, format: v === 'none' ? '' : v })}
                                >
                                    <SelectTrigger>
                                        <SelectValue placeholder="Select format" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="none">Not specified</SelectItem>
                                        {getFormatOptions(formData.media_type).map((opt) => (
                                            <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-2">
                                <label className="text-sm font-medium">Sony Master Ref</label>
                                <Input
                                    value={formData.sony_master_ref}
                                    onChange={(e) => setFormData({ ...formData, sony_master_ref: e.target.value })}
                                />
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowEditModal(false)}>
                            Cancel
                        </Button>
                        <Button onClick={handleUpdate} disabled={isSaving}>
                            {isSaving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving...</> : 'Save Changes'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Import Modal */}
            <Dialog open={showImportModal} onOpenChange={(open) => { setShowImportModal(open); if (!open) resetImport(); }}>
                <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>Import Catalogue Entries</DialogTitle>
                    </DialogHeader>
                    
                    <div className="space-y-4 py-4">
                        {/* File Upload */}
                        <div className="space-y-2">
                            <label className="text-sm font-medium">Upload File</label>
                            <div className="border-2 border-dashed border-border rounded-lg p-6 text-center">
                                {importFile ? (
                                    <div className="flex items-center justify-center gap-3">
                                        <FileSpreadsheet className="h-8 w-8 text-emerald-500" />
                                        <div className="text-left">
                                            <p className="font-medium text-slate-700">{importFile.name}</p>
                                            <p className="text-xs text-slate-400">{(importFile.size / 1024).toFixed(1)} KB</p>
                                        </div>
                                        {isValidating && <Loader2 className="h-5 w-5 animate-spin text-slate-400" />}
                                        <Button variant="ghost" size="sm" onClick={() => { setImportFile(null); setValidationReport(null); setImportResult(null); }}>
                                            <X className="h-4 w-4" />
                                        </Button>
                                    </div>
                                ) : (
                                    <>
                                        <Upload className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                                        <p className="text-sm text-slate-500 mb-2">
                                            Click below to select a file
                                        </p>
                                        <input
                                            ref={importFileInputRef}
                                            type="file"
                                            accept=".csv,.xlsx,.xls"
                                            onChange={handleFileSelect}
                                            className="hidden"
                                            data-testid="import-file-input"
                                        />
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => importFileInputRef.current?.click()}
                                            data-testid="import-file-select-btn"
                                        >
                                            Select File
                                        </Button>
                                        <p className="text-xs text-slate-400 mt-2">
                                            CSV or XLSX. Required: catalogue_number, media_type
                                        </p>
                                    </>
                                )}
                            </div>
                        </div>

                        {/* Validation Report */}
                        {validationReport && !importResult && (
                            <div className="space-y-3" data-testid="validation-report">
                                {/* Column Mapping */}
                                <div className="bg-slate-50 rounded-lg p-3">
                                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Column Mapping</p>
                                    <div className="flex flex-wrap gap-1.5">
                                        {validationReport.columns_expected.map(col => {
                                            const found = validationReport.columns_found.includes(col);
                                            return (
                                                <span key={col} className={`px-2 py-0.5 rounded text-xs font-mono ${found ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-600'}`}>
                                                    {col} {found ? '\u2713' : '\u2717'}
                                                </span>
                                            );
                                        })}
                                    </div>
                                </div>

                                {/* Summary */}
                                <div className="grid grid-cols-5 gap-2 text-center">
                                    <div className="p-2 bg-white border rounded">
                                        <p className="text-lg font-bold text-slate-700">{validationReport.summary.valid_rows}</p>
                                        <p className="text-[10px] text-slate-500">Valid</p>
                                    </div>
                                    <div className="p-2 bg-white border rounded">
                                        <p className="text-lg font-bold text-red-600">{validationReport.summary.error_rows}</p>
                                        <p className="text-[10px] text-slate-500">Errors</p>
                                    </div>
                                    <div className="p-2 bg-white border rounded">
                                        <p className="text-lg font-bold text-amber-600">{validationReport.summary.warning_rows}</p>
                                        <p className="text-[10px] text-slate-500">Warnings</p>
                                    </div>
                                    <div className="p-2 bg-white border rounded">
                                        <p className="text-lg font-bold text-emerald-600">{validationReport.summary.new_entries}</p>
                                        <p className="text-[10px] text-slate-500">New</p>
                                    </div>
                                    <div className="p-2 bg-white border rounded">
                                        <p className="text-lg font-bold text-blue-600">{validationReport.summary.existing_entries}</p>
                                        <p className="text-[10px] text-slate-500">Existing</p>
                                    </div>
                                </div>

                                {/* Unknown SKUs */}
                                {validationReport.summary.unknown_skus?.length > 0 && (
                                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                                        <p className="text-xs font-semibold text-amber-700 mb-1">
                                            <AlertCircle className="h-3 w-3 inline mr-1" />
                                            Unknown SKUs ({validationReport.summary.unknown_skus.length})
                                        </p>
                                        <p className="text-xs text-amber-600">
                                            {validationReport.summary.unknown_skus.join(', ')}
                                        </p>
                                    </div>
                                )}

                                {/* Row Details (first 10) */}
                                <div className="border rounded-lg overflow-hidden">
                                    <Table>
                                        <TableHeader>
                                            <TableRow className="bg-slate-50">
                                                <TableHead className="w-[40px] text-xs">Row</TableHead>
                                                <TableHead className="text-xs">Cat#</TableHead>
                                                <TableHead className="text-xs">Type</TableHead>
                                                <TableHead className="text-xs">Primary SKU</TableHead>
                                                <TableHead className="text-xs">Components</TableHead>
                                                <TableHead className="text-xs">Services</TableHead>
                                                <TableHead className="text-xs w-[60px]">Status</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {validationReport.validated_rows.slice(0, 15).map(r => (
                                                <TableRow key={r.row_number} className={r.errors.length > 0 ? 'bg-red-50/50' : r.warnings.length > 0 ? 'bg-amber-50/50' : ''}>
                                                    <TableCell className="text-xs text-slate-400">{r.row_number}</TableCell>
                                                    <TableCell className="text-xs font-mono font-medium">{r.catalogue_number || '—'}</TableCell>
                                                    <TableCell className="text-xs">{r.media_type || '—'}</TableCell>
                                                    <TableCell className="text-xs font-mono">{r.sku_primary || '—'}</TableCell>
                                                    <TableCell className="text-xs font-mono">{r.sku_components?.join('; ') || '—'}</TableCell>
                                                    <TableCell className="text-xs font-mono">{r.sku_services?.join('; ') || '—'}</TableCell>
                                                    <TableCell>
                                                        {r.errors.length > 0 ? (
                                                            <span className="text-[10px] text-red-600" title={r.errors.join(', ')}>
                                                                <XCircle className="h-3.5 w-3.5" />
                                                            </span>
                                                        ) : r.warnings.length > 0 ? (
                                                            <span className="text-[10px] text-amber-600" title={r.warnings.join(', ')}>
                                                                <AlertTriangle className="h-3.5 w-3.5" />
                                                            </span>
                                                        ) : (
                                                            <span className="text-[10px] text-emerald-600">
                                                                <CheckCircle className="h-3.5 w-3.5" />
                                                            </span>
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                    {validationReport.validated_rows.length > 15 && (
                                        <p className="text-xs text-slate-400 text-center py-2">
                                            +{validationReport.validated_rows.length - 15} more rows
                                        </p>
                                    )}
                                </div>

                                {/* Error details */}
                                {validationReport.validated_rows.some(r => r.errors.length > 0 || r.warnings.length > 0) && (
                                    <div className="bg-white border rounded-lg p-3 max-h-32 overflow-y-auto">
                                        <p className="text-xs font-semibold text-slate-500 mb-1.5">Issues</p>
                                        {validationReport.validated_rows.filter(r => r.errors.length > 0 || r.warnings.length > 0).slice(0, 20).map(r => (
                                            <div key={r.row_number} className="text-xs mb-1">
                                                {r.errors.map((e, i) => (
                                                    <p key={`e${i}`} className="text-red-600">Row {r.row_number}: {e}</p>
                                                ))}
                                                {r.warnings.map((w, i) => (
                                                    <p key={`w${i}`} className="text-amber-600">Row {r.row_number}: {w}</p>
                                                ))}
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {/* Import Options */}
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <label className="text-sm font-medium">If duplicate catalogue# exists:</label>
                                        <Select value={duplicateBehavior} onValueChange={setDuplicateBehavior}>
                                            <SelectTrigger className="w-full" data-testid="duplicate-behavior-select">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="update">Update existing</SelectItem>
                                                <SelectItem value="skip">Skip (keep existing)</SelectItem>
                                                <SelectItem value="error">Abort on duplicates</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-sm font-medium">SKU links on existing entries:</label>
                                        <Select value={skuLinkBehavior} onValueChange={setSkuLinkBehavior}>
                                            <SelectTrigger className="w-full" data-testid="sku-link-behavior-select">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="replace">Replace all SKU links</SelectItem>
                                                <SelectItem value="merge">Merge (add new, keep existing)</SelectItem>
                                                <SelectItem value="skip">Skip (don't modify SKU links)</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Import Result */}
                        {importResult && (
                            <div className={`rounded-lg p-4 ${(importResult.errors?.length > 0 || importResult.warnings?.length > 0) ? 'bg-amber-50' : 'bg-emerald-50'}`} data-testid="import-result">
                                <p className="font-medium mb-2">
                                    {importResult.errors?.length > 0 ? (
                                        <span className="text-amber-800">Import completed with issues</span>
                                    ) : (
                                        <span className="text-emerald-800">Import successful!</span>
                                    )}
                                </p>
                                <div className="grid grid-cols-3 gap-4 text-sm mb-3">
                                    <div className="text-center p-2 bg-white rounded">
                                        <p className="text-lg font-bold text-emerald-600">{importResult.created}</p>
                                        <p className="text-xs text-slate-500">Created</p>
                                    </div>
                                    <div className="text-center p-2 bg-white rounded">
                                        <p className="text-lg font-bold text-blue-600">{importResult.updated}</p>
                                        <p className="text-xs text-slate-500">Updated</p>
                                    </div>
                                    <div className="text-center p-2 bg-white rounded">
                                        <p className="text-lg font-bold text-slate-400">{importResult.skipped}</p>
                                        <p className="text-xs text-slate-500">Skipped</p>
                                    </div>
                                </div>
                                
                                {(importResult.errors?.length > 0 || importResult.warnings?.length > 0) && (
                                    <div className="mt-3 bg-white rounded p-3 max-h-32 overflow-y-auto">
                                        {importResult.errors?.length > 0 && (
                                            <>
                                                <p className="text-xs font-medium text-red-600 mb-1">
                                                    <AlertCircle className="h-3 w-3 inline mr-1" />
                                                    {importResult.errors.length} error(s):
                                                </p>
                                                {importResult.errors.slice(0, 10).map((err, i) => (
                                                    <p key={`e${i}`} className="text-xs text-slate-600">
                                                        Row {err.row}: {err.message}
                                                    </p>
                                                ))}
                                            </>
                                        )}
                                        {importResult.warnings?.length > 0 && (
                                            <>
                                                <p className="text-xs font-medium text-amber-600 mb-1 mt-2">
                                                    <AlertTriangle className="h-3 w-3 inline mr-1" />
                                                    {importResult.warnings.length} warning(s):
                                                </p>
                                                {importResult.warnings.slice(0, 10).map((w, i) => (
                                                    <p key={`w${i}`} className="text-xs text-slate-600">
                                                        Row {w.row}: {w.message}
                                                    </p>
                                                ))}
                                            </>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={() => { setShowImportModal(false); resetImport(); }}>
                            {importResult ? 'Close' : 'Cancel'}
                        </Button>
                        {!importResult && validationReport && (
                            <Button
                                onClick={handleImport}
                                disabled={!importFile || isImporting || validationReport.summary.valid_rows === 0}
                                data-testid="import-submit-btn"
                            >
                                {isImporting ? (
                                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Importing...</>
                                ) : (
                                    <><Upload className="h-4 w-4 mr-2" />Import {validationReport.summary.valid_rows} row(s)</>
                                )}
                            </Button>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation */}
            <Dialog open={!!showDeleteConfirm} onOpenChange={() => setShowDeleteConfirm(null)}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>Delete Catalogue Entry</DialogTitle>
                    </DialogHeader>
                    <div className="py-4">
                        <p className="text-slate-600">
                            Are you sure you want to delete{' '}
                            <strong>{showDeleteConfirm?.catalogue_number}</strong>
                            {showDeleteConfirm?.title && ` (${showDeleteConfirm.title})`}?
                        </p>
                        <p className="text-sm text-slate-400 mt-2">
                            This action cannot be undone.
                        </p>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowDeleteConfirm(null)}>
                            Cancel
                        </Button>
                        <Button 
                            variant="destructive" 
                            onClick={() => handleDelete(showDeleteConfirm?.id)}
                            disabled={isDeleting}
                        >
                            {isDeleting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Deleting...</> : 'Delete'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

        </div>
    );
}

// ==================== Invoices & Credit Tab ====================
function InvoicesCreditTab({ company, onUpdate }) {
    const [invoices, setInvoices] = useState([]);
    const [creditStatus, setCreditStatus] = useState(null);
    const [syncLogs, setSyncLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [syncing, setSyncing] = useState(false);
    const [filter, setFilter] = useState('all');
    const [editingLimit, setEditingLimit] = useState(false);
    const [creditLimitValue, setCreditLimitValue] = useState('');
    const [downloading, setDownloading] = useState(null);

    // Shared reminder UX — same modal + POST + toast pipeline as the
    // customer-side Invoices page. On a successful send we refetch so
    // the new `last_reminder_sent_at` appears immediately under the
    // Overdue status badge.
    const { openReminderModal, ReminderModal } = useInvoiceReminder({
        onSent: () => fetchData(),
    });

    const fmtCurrency = (amount, currency = company?.currency || 'DKK') => {
        if (amount == null) return '-';
        return new Intl.NumberFormat('da-DK', { style: 'currency', currency }).format(amount);
    };

    const fmtDate = (d) => {
        if (!d) return '-';
        return new Date(d).toLocaleDateString('da-DK', { year: 'numeric', month: 'short', day: 'numeric' });
    };

    const fetchData = async () => {
        setLoading(true);
        try {
            const [invRes, creditRes, logsRes] = await Promise.all([
                invoiceSyncApi.getInvoices(company.id, filter !== 'all' ? filter : undefined).catch(() => ({ data: { invoices: [] } })),
                invoiceSyncApi.getCreditStatus(company.id).catch(() => ({ data: null })),
                invoiceSyncApi.getSyncLogs(company.id, 5).catch(() => ({ data: { logs: [] } })),
            ]);
            setInvoices(invRes.data.invoices || []);
            setCreditStatus(creditRes.data);
            setSyncLogs(logsRes.data.logs || []);
        } catch (err) {
            console.error('Failed to load invoice data', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchData(); }, [company.id, filter]);

    const handleSync = async () => {
        setSyncing(true);
        try {
            const { data } = await invoiceSyncApi.triggerSync(company.id);
            toast.success(`Synced ${data.invoices_synced} invoice(s) from e-conomic`);
            fetchData();
            if (onUpdate) onUpdate();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Sync failed');
        } finally {
            setSyncing(false);
        }
    };

    const handleSaveCreditLimit = async () => {
        const val = parseFloat(creditLimitValue);
        if (isNaN(val) || val < 0) { toast.error('Enter a valid positive amount'); return; }
        try {
            await invoiceSyncApi.setCreditLimit(company.id, val);
            toast.success(`Credit limit set to ${fmtCurrency(val)}`);
            setEditingLimit(false);
            fetchData();
            if (onUpdate) onUpdate();
        } catch (err) {
            toast.error('Failed to save credit limit');
        }
    };

    const handleRemoveCreditLimit = async () => {
        try {
            await invoiceSyncApi.removeCreditLimit(company.id);
            toast.success('Credit limit removed');
            setEditingLimit(false);
            fetchData();
            if (onUpdate) onUpdate();
        } catch (err) {
            toast.error('Failed to remove credit limit');
        }
    };

    const handleDownloadPdf = async (invoiceNumber) => {
        setDownloading(invoiceNumber);
        try {
            const response = await invoiceSyncApi.getInvoicePdf(company.id, invoiceNumber);
            const blob = response.data;
            
            if (blob.type === 'application/json') {
                const text = await blob.text();
                const json = JSON.parse(text);
                if (json.download_url) { window.open(json.download_url, '_blank'); return; }
                toast.error(json.detail || 'PDF not available');
                return;
            }
            
            const url = URL.createObjectURL(blob);
            window.open(url, '_blank');
            setTimeout(() => URL.revokeObjectURL(url), 60000);
        } catch (err) {
            const detail = err.response?.data instanceof Blob
                ? await err.response.data.text().then(t => { try { return JSON.parse(t).detail; } catch { return t; } })
                : err.response?.data?.detail;
            toast.error(detail || 'Could not download invoice PDF');
        } finally {
            setDownloading(null);
        }
    };

    if (loading) return <div className="text-center py-12 text-slate-400">Loading invoices & credit data...</div>;

    const isBlocked = creditStatus?.is_blocked;
    const creditLimit = creditStatus?.credit_limit;
    const outstanding = creditStatus?.outstanding_balance ?? 0;
    const overdue = creditStatus?.overdue_balance ?? 0;
    const currency = creditStatus?.currency || company?.currency || 'DKK';

    return (
        <div className="space-y-6" data-testid="invoices-credit-tab">
            {/* Sync Button */}
            <div className="flex items-center justify-between">
                <h2 className="text-base font-semibold text-slate-900">Invoices & Credit</h2>
                <Button
                    variant="outline"
                    size="sm"
                    onClick={handleSync}
                    disabled={syncing}
                    data-testid="company-sync-btn"
                >
                    <RefreshCw className={`h-4 w-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
                    {syncing ? 'Syncing...' : 'Sync with e-conomic'}
                </Button>
            </div>

            {/* Credit Summary */}
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                <div className="bg-white border rounded-lg p-4" data-testid="credit-outstanding">
                    <p className="text-xs font-medium text-slate-500 uppercase mb-1">Outstanding</p>
                    <p className="text-lg font-bold">{fmtCurrency(outstanding, currency)}</p>
                </div>
                <div className="bg-white border rounded-lg p-4" data-testid="credit-overdue">
                    <p className="text-xs font-medium text-slate-500 uppercase mb-1">Overdue</p>
                    <p className={`text-lg font-bold ${overdue > 0 ? 'text-red-600' : ''}`}>{fmtCurrency(overdue, currency)}</p>
                    {(() => {
                        const maxDays = invoices.reduce((max, inv) => Math.max(max, inv.days_overdue || 0), 0);
                        return maxDays > 0 ? <p className="text-[10px] text-red-400 mt-0.5">Oldest: {maxDays}d overdue</p> : null;
                    })()}
                </div>
                <div className="bg-white border rounded-lg p-4" data-testid="credit-limit-display">
                    <p className="text-xs font-medium text-slate-500 uppercase mb-1">Credit Limit</p>
                    <p className="text-lg font-bold">{creditLimit != null ? fmtCurrency(creditLimit, currency) : 'Not set'}</p>
                </div>
                <div className="bg-white border rounded-lg p-4" data-testid="credit-status-display">
                    <p className="text-xs font-medium text-slate-500 uppercase mb-1">Status</p>
                    {isBlocked ? (
                        <span className="inline-flex items-center gap-1.5 text-red-700 font-bold text-sm">
                            <Ban className="h-4 w-4" /> Blocked
                        </span>
                    ) : overdue > 0 ? (
                        <span className="inline-flex items-center gap-1.5 text-amber-600 font-bold text-sm">
                            <AlertTriangle className="h-4 w-4" /> Warning
                        </span>
                    ) : (
                        <span className="inline-flex items-center gap-1.5 text-emerald-600 font-bold text-sm">
                            <CheckCircle className="h-4 w-4" /> OK
                        </span>
                    )}
                </div>
                <div className="bg-white border rounded-lg p-4" data-testid="credit-last-sync">
                    <p className="text-xs font-medium text-slate-500 uppercase mb-1">Last Sync</p>
                    <p className="text-sm font-medium">{creditStatus?.last_sync ? fmtDate(creditStatus.last_sync) : 'Never'}</p>
                </div>
            </div>

            {/* Blocked Warning */}
            {isBlocked && (
                <div className="flex items-center gap-3 bg-red-50 border border-red-200 rounded-lg p-4" data-testid="credit-blocked-alert">
                    <Ban className="h-5 w-5 text-red-600 flex-shrink-0" />
                    <div className="flex-1">
                        <p className="font-medium text-red-800 text-sm">New order submission is blocked for this company</p>
                        <p className="text-xs text-red-600 mt-0.5">{creditStatus?.block_reason}</p>
                    </div>
                </div>
            )}

            {/* Credit Limit Controls */}
            <div className="bg-white border rounded-lg p-4" data-testid="credit-limit-controls">
                <div className="flex items-center justify-between">
                    <div>
                        <h3 className="text-sm font-semibold text-slate-900">Credit Limit Management</h3>
                        <p className="text-xs text-slate-500 mt-0.5">
                            {creditLimit != null
                                ? `Current limit: ${fmtCurrency(creditLimit, currency)}. Orders blocked when outstanding exceeds this.`
                                : 'No credit limit set. Orders are never blocked by outstanding balance.'}
                        </p>
                    </div>
                    {!editingLimit ? (
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                                setCreditLimitValue(creditLimit != null ? String(creditLimit) : '');
                                setEditingLimit(true);
                            }}
                            data-testid="edit-credit-limit-btn"
                        >
                            <Edit className="h-3.5 w-3.5 mr-1.5" />
                            {creditLimit != null ? 'Edit Limit' : 'Set Limit'}
                        </Button>
                    ) : (
                        <div className="flex items-center gap-2">
                            <Input
                                type="number"
                                min="0"
                                step="1000"
                                value={creditLimitValue}
                                onChange={(e) => setCreditLimitValue(e.target.value)}
                                placeholder="e.g. 50000"
                                className="w-36 h-8 text-sm"
                                data-testid="credit-limit-input"
                            />
                            <Button size="sm" className="h-8" onClick={handleSaveCreditLimit} data-testid="save-credit-limit-btn">Save</Button>
                            {creditLimit != null && (
                                <Button variant="outline" size="sm" className="h-8 text-red-600 hover:text-red-700" onClick={handleRemoveCreditLimit} data-testid="remove-credit-limit-btn">
                                    Remove
                                </Button>
                            )}
                            <Button variant="ghost" size="sm" className="h-8" onClick={() => setEditingLimit(false)}>Cancel</Button>
                        </div>
                    )}
                </div>
            </div>

            {/* Invoice Filter Tabs */}
            <div className="flex items-center gap-2" data-testid="invoice-filter-row">
                <span className="text-sm font-medium text-slate-600 mr-2">Filter:</span>
                {['all', 'unpaid', 'overdue', 'paid'].map((f) => (
                    <Button
                        key={f}
                        variant={filter === f ? 'default' : 'outline'}
                        size="sm"
                        className="h-7 text-xs"
                        onClick={() => setFilter(f)}
                        data-testid={`company-filter-${f}`}
                    >
                        {f === 'all' ? `All (${invoices.length})` : f.charAt(0).toUpperCase() + f.slice(1)}
                    </Button>
                ))}
            </div>

            {/* Invoice Table */}
            {invoices.length === 0 ? (
                <div className="text-center py-8 text-slate-400 bg-white border rounded-lg" data-testid="no-invoices-msg">
                    <FileText className="h-10 w-10 mx-auto mb-2 opacity-30" />
                    <p className="text-sm">No invoices found for this company</p>
                    {!creditStatus?.last_sync && <p className="text-xs mt-1">Click "Sync with e-conomic" to fetch invoices</p>}
                </div>
            ) : (
                <div className="bg-white border rounded-lg overflow-hidden">
                    <Table data-testid="company-invoices-table">
                        <TableHeader>
                            <TableRow className="bg-muted/50">
                                <TableHead className="text-xs">Invoice #</TableHead>
                                <TableHead className="text-xs">Owntune Ref</TableHead>
                                <TableHead className="text-xs">Invoice Date</TableHead>
                                <TableHead className="text-xs">Due Date</TableHead>
                                <TableHead className="text-xs text-right">Amount</TableHead>
                                <TableHead className="text-xs text-right">Outstanding</TableHead>
                                <TableHead className="text-xs text-center">Status</TableHead>
                                <TableHead className="text-xs text-center w-[60px]">PDF</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {invoices.map((inv) => {
                                const statusCfg = {
                                    paid: 'bg-emerald-100 text-emerald-700',
                                    unpaid: 'bg-amber-100 text-amber-700',
                                    overdue: 'bg-red-100 text-red-700',
                                };
                                return (
                                    <TableRow key={inv.id || inv.economic_invoice_number} data-testid={`company-invoice-row-${inv.economic_invoice_number}`}>
                                        <TableCell className="font-medium text-sm">{inv.economic_invoice_number}</TableCell>
                                        <TableCell className="text-sm text-slate-500">{inv.references_other || '-'}</TableCell>
                                        <TableCell className="text-sm">{fmtDate(inv.date)}</TableCell>
                                        <TableCell className="text-sm">{fmtDate(inv.due_date)}</TableCell>
                                        <TableCell className="text-sm text-right font-medium">{fmtCurrency(inv.gross_amount, inv.currency)}</TableCell>
                                        <TableCell className="text-sm text-right font-medium">
                                            {inv.remainder > 0 ? fmtCurrency(inv.remainder, inv.currency) : '-'}
                                        </TableCell>
                                        <TableCell className="text-center">
                                            <div className="flex flex-col items-center gap-0.5">
                                                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${statusCfg[inv.payment_status] || statusCfg.unpaid}`}>
                                                    {inv.payment_status ? inv.payment_status.charAt(0).toUpperCase() + inv.payment_status.slice(1) : 'Unknown'}
                                                </span>
                                                {inv.payment_status === 'overdue' && inv.days_overdue != null && (
                                                    <span className="text-[10px] text-red-400 font-medium">{inv.days_overdue}d overdue</span>
                                                )}
                                                {inv.payment_status === 'overdue' && inv.last_reminder_sent_at && (
                                                    <span
                                                        className="text-[10px] text-slate-500"
                                                        data-testid={`last-reminder-${inv.economic_invoice_number}`}
                                                        title={`Sent by ${inv.last_reminder_sent_by_name || 'admin'}`}
                                                    >
                                                        Reminder sent {fmtDate(inv.last_reminder_sent_at)}
                                                    </span>
                                                )}
                                            </div>
                                        </TableCell>
                                        <TableCell className="text-center">
                                            <div className="flex items-center justify-center gap-1">
                                                {inv.payment_status === 'overdue' && (
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        className="h-7 w-7 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                                                        onClick={() => openReminderModal(inv)}
                                                        data-testid={`company-send-reminder-${inv.economic_invoice_number}`}
                                                        title="Send payment reminder"
                                                    >
                                                        <Bell className="h-3.5 w-3.5" />
                                                    </Button>
                                                )}
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className="h-7 w-7 p-0"
                                                    onClick={() => handleDownloadPdf(inv.economic_invoice_number)}
                                                    disabled={downloading === inv.economic_invoice_number}
                                                    data-testid={`company-pdf-${inv.economic_invoice_number}`}
                                                >
                                                    <Download className="h-3.5 w-3.5" />
                                                </Button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </div>
            )}

            {/* Sync Logs */}
            {syncLogs.length > 0 && (
                <div className="bg-white border rounded-lg p-4" data-testid="sync-logs-section">
                    <h3 className="text-sm font-semibold text-slate-900 mb-3">Recent Sync History</h3>
                    <div className="space-y-2">
                        {syncLogs.map((log) => (
                            <div key={log.id} className="flex items-center justify-between text-xs border-b last:border-0 pb-2 last:pb-0">
                                <div className="flex items-center gap-2">
                                    <span className={`w-2 h-2 rounded-full ${log.status === 'success' ? 'bg-emerald-500' : 'bg-red-500'}`} />
                                    <span className="text-slate-600">{fmtDate(log.synced_at)}</span>
                                    <span className="text-slate-400">
                                        {new Date(log.synced_at).toLocaleTimeString('da-DK', { hour: '2-digit', minute: '2-digit' })}
                                    </span>
                                </div>
                                <div className="flex items-center gap-3 text-slate-500">
                                    {log.status === 'success' ? (
                                        <>
                                            <span>{log.invoices_synced} invoice(s)</span>
                                            <span>Outstanding: {fmtCurrency(log.total_outstanding, log.currency)}</span>
                                        </>
                                    ) : (
                                        <span className="text-red-500">{log.error_message || 'Failed'}</span>
                                    )}
                                    <span className="text-slate-400">{log.triggered_by?.split(':')[0]}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Shared payment-reminder modal — same UX as the customer-facing
                Invoices page (see hooks/useInvoiceReminder). Rendered here at
                the tab root so it overlays cleanly no matter which row was
                clicked. */}
            {ReminderModal}
        </div>
    );
}

// ==================== Inbound Activity Tab ====================
function InboundTab({ company }) {
    const navigate = useNavigate();
    const [documents, setDocuments] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        fetchDocuments();
    }, [company.id]);

    const fetchDocuments = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(
                `${API_BASE}/api/organizations/${company.id}/inbound-documents`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setDocuments(response.data || []);
        } catch (error) {
            // May not have any documents
        } finally {
            setIsLoading(false);
        }
    };

    const getStatusBadge = (status) => {
        const config = {
            linked: { icon: CheckCircle, color: 'text-emerald-600' },
            needs_review: { icon: AlertTriangle, color: 'text-amber-600' },
            pending: { icon: Clock, color: 'text-blue-600' },
            failed: { icon: XCircle, color: 'text-red-600' },
        };
        const c = config[status] || config.pending;
        const Icon = c.icon;
        return <Icon className={`h-4 w-4 ${c.color}`} />;
    };

    if (isLoading) {
        return <div className="text-center py-8 text-slate-400">Loading inbound activity...</div>;
    }

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <p className="text-sm text-slate-500">
                    {documents.length} document(s) linked to orders from this company
                </p>
                <Button variant="outline" size="sm" onClick={() => navigate('/admin/inbound-documents')}>
                    View All Inbound Documents
                </Button>
            </div>

            {documents.length === 0 ? (
                <div className="border rounded-lg p-8 text-center">
                    <Inbox className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500">No inbound documents for this company yet</p>
                </div>
            ) : (
                <div className="border rounded-lg overflow-hidden">
                    <Table>
                        <TableHeader>
                            <TableRow className="bg-background">
                                <TableHead className="w-[40px]"></TableHead>
                                <TableHead>Document</TableHead>
                                <TableHead className="w-[120px]">Type</TableHead>
                                <TableHead className="w-[150px]">Order</TableHead>
                                <TableHead className="w-[120px]">Received</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {documents.slice(0, 20).map((doc) => (
                                <TableRow key={doc.id} className="hover:bg-background">
                                    <TableCell>{getStatusBadge(doc.status)}</TableCell>
                                    <TableCell>
                                        <p className="text-sm font-medium truncate max-w-[300px]">
                                            {doc.subject || doc.from_email || doc.external_id}
                                        </p>
                                    </TableCell>
                                    <TableCell>
                                        <span className="text-xs bg-slate-100 px-2 py-0.5 rounded">
                                            {doc.document_type}
                                        </span>
                                    </TableCell>
                                    <TableCell className="text-sm text-slate-600">
                                        #{doc.matched_order_number?.toString().padStart(6, '0') || '—'}
                                    </TableCell>
                                    <TableCell className="text-sm text-slate-500">
                                        {new Date(doc.received_at).toLocaleDateString('en-GB')}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            )}
        </div>
    );
}

// ==================== Main Component ====================
export default function CompaniesPage({ embedded = false } = {}) {
    const { companyId } = useParams();

    if (companyId) {
        return <CompanyDetailView companyId={companyId} />;
    }

    return <CompanyListView embedded={embedded} />;
}
